// Russian for the sales namespace (Module 09 §4, slice P3-A part 2) — the
// design partner's working language, and the language a KazKabel sales manager
// prices cable and talks to Казэнергокомплект in.
//
// COMMERCIAL REGISTER, not generic UI Russian: прайс-лист/ценовая категория for
// the price book, кредитный стоп/отсрочка платежа at the credit cell, обрезок/
// скидка на обрезки at the off-cut lane, маржа/порог маржи for the guard, НДС
// for the tax mode — the words a Kazakh cable plant's commercial department
// already uses on paper and in 1С.
//
// THE THREE WORDS SETTLED IN BOTH LANGUAGES AT ONCE (the full reasoning is in
// sales.i18n.ts, restated here because a term settled in one language is not
// settled): tier → «ценовая категория», credit hold → «кредитный стоп», margin
// guard → «порог маржи». Plus off-cut → «обрезок» (module 05's remnant registry
// says the same word for the same physical thing) and price list →
// «прайс-лист».
//
// COUNTED NOUNS ARE NOT AGREED WITH THEIR NUMBER (D-030): «Дней: 30», «Строк
// принято: 12» — a colon form that is correct for 1, 2 and 5 alike, instead of
// three plural branches a dictionary cannot express.
//
// The type is enforced by `SalesDict` in sales.i18n.ts: a missing or extra key
// fails `tsc`, and tools/ci/i18n-parity.ts fails if any value here regresses to
// its English text (the handful that are legitimately identical — a proper
// noun, a percent sign, a pure interpolation, a machine-syntax example — are
// declared in src/i18n-completeness.ts's IDENTICAL_BY_DESIGN.ru).

import type { SalesDict } from "./sales.i18n.js";

export const salesRuDict: SalesDict = {
  nav: {
    aria: "Разделы продаж",
    customers: "Клиенты",
    quotes: "Предложения",
    orders: "Заказы",
    reviews: "На согласовании",
    priceLists: "Прайс-листы",
    offcutClasses: "Скидки на обрезки",
    marginGuards: "Пороги маржи",
    tasks: "Задачи",
    settings: "Настройки",
    analytics: "Аналитика",
    pipeline: "Воронка",
    general: "Общие",
    groupAria: {
      tasks: "Задачи и согласования",
      settings: "Настройки продаж",
    },
  },

  common: {
    loading: "Загрузка…",
    filters: "Фильтры",
    filtersCount: "Фильтры ({{count}})",
    filtersTitle: "Фильтры",
    filtersDone: "Готово",
    filterRemove: "Убрать: {{filter}}",
    filtersClear: "Сбросить все",
    filterChip: "{{filter}} — {{value}}",
    otherLanguages: "Другие языки",
    filterCustomerClear: "Показать всех клиентов",
    retry: "Повторить",
    requestId: "Идентификатор запроса",
    serverSaid: "Отклонено: {{title}}",
    busyRetry: "Прямо сейчас это меняет кто-то другой. Повторите через мгновение.",
    loadMore: "Показать ещё",
    pageRange: "Показаны {{from}}–{{to}}",
    pageRangeOf: "Показаны {{from}}–{{to}} из {{total}}",
    pagePrev: "Предыдущая страница",
    pageNext: "Следующая страница",
    close: "Закрыть",
    cancel: "Отмена",
    save: "Сохранить",
    saving: "Сохранение…",
    edit: "Изменить",
    notListed: "Нет в этом списке",
  },

  access: {
    title: "Недоступно для вашей роли",
    area: "В разделе продаж нет экранов для вашей роли. Обратитесь к администратору группы.",
    customers: "Клиентская база — для продаж, финансов, планирования и администраторов.",
    importCustomers: "Загрузка клиентов доступна продажам и администраторам.",
    importOrders: "Загрузка открытых заказов доступна продажам и администраторам.",
    pricing: "Прайс-листы и скидки — для продаж и администраторов.",
    pricingValue: "Цены этого прайс-листа не показываются для вашей роли.",
    guards: "Пороги маржи относятся к управлению ценами — их настраивает администратор группы.",
    quotes:
      "Журнал предложений — для тех, кто их составляет: менеджеров по продажам и администраторов.",
    reviews: "Согласования — для тех, кто принимает решения по марже и кредитным стопам.",
    orders: "Заказы доступны продажам, планированию, финансам, ОТК и администраторам.",
    tasks: "Задачи — для менеджеров по продажам и администраторов.",
    settings: "Настройки продаж доступны всем, кто просматривает заказы.",
    analytics:
      "Аналитика продаж — для тех, кто ведёт предложения и заказы: менеджеров по продажам и администраторов.",
    pipeline: "Воронка продаж — для продаж, финансов, планирования и администраторов.",
  },

  tier: {
    market: "Розница",
    b2b: "B2B",
    distributor: "Дистрибьютор",
  },

  channel: {
    phone: "Телефон",
    telegram: "Telegram",
    whatsapp: "WhatsApp",
    email: "Эл. почта",
  },

  consent: {
    unknown: "Согласие не подтверждено",
    opted_in: "Согласие получено",
    opted_out: "Отказ от рассылки",
  },

  notification: {
    colKind: "Сообщение",
    colChannel: "Канал",
    colStatus: "Доставка",
    colWhen: "Когда",
    kind: {
      received: "Запрос получен",
      order_confirmed: "Заказ подтверждён",
      order_in_production: "Заказ в производстве",
      order_ready: "Заказ готов",
      order_shipped: "Заказ отгружен",
      order_cancelled: "Заказ отменён",
      quote_sent: "Предложение отправлено",
      quote_expiring: "Предложение скоро истекает",
      ready_not_collected: "Напоминание: заказ готов к вывозу",
      quote_follow_up: "Напоминание о предложении",
    },
    state: {
      queued: "Отправляется…",
      held: "Отложено",
      sent: "Отправлено",
      delivered: "Доставлено",
      failed: "Ошибка",
      skipped_no_channel: "Не отправлено: нет способа связи",
      skipped_opted_out: "Не отправлено: клиент отписался",
      skipped_superseded: "Не отправлено: устарело",
      skipped_customer_archived: "Не отправлено: клиент в архиве",
    },
    heldUntil: "Отложено до {{when}} (тихие часы).",
    heldUntilUnknown: "Отложено до конца тихих часов.",
    retryAt: "Повторная попытка в {{when}}.",
    retryScheduled: "Запланирована повторная попытка.",
    superseded: "Сообщение устарело из-за более позднего обновления, поэтому не отправлено.",
    noChannel:
      "У клиента не привязан ни Telegram, ни WhatsApp. Привяжите один из них на вкладке «Контакты и каналы», чтобы следующее обновление дошло до клиента.",
    reason: {
      TEMPLATE_NOT_FOUND:
        "Шаблон этого сообщения отсутствует, отключён или не имеет версии для этого мессенджера. Проверьте «Шаблоны сообщений».",
      TEMPLATE_VARS_INVALID: "Шаблону сообщения нужны данные, которых в этом сообщении нет.",
      SEND_ERROR: "Сообщение не удалось передать на отправку.",
      DELIVERY_FAILED: "Мессенджер не доставил сообщение.",
      other: "Сообщение не удалось отправить.",
    },
  },

  offline: {
    title: "Нет сети",
    body: "Нет сети. Страница обновится, когда связь вернётся.",
  },

  customers: {
    title: "Клиенты",
    new: "Новый клиент",
    search: "Поиск клиентов",
    searchHintWithPhone: "Название, номер, БИН/ИИН или телефон",
    searchHintNoPhone: "Название, номер или БИН/ИИН",
    filterTier: "Ценовая категория",
    filterStatus: "Статус",
    filterOwner: "Ответственный",
    filterCreditHold: "Кредитный стоп",
    anyTier: "Любая категория",
    anyStatus: "Любой статус",
    anyOwner: "Любой ответственный",
    anyCreditHold: "Стоп: любой",
    creditHoldOn: "На кредитном стопе",
    creditHoldOff: "Без стопа",
    statusActive: "Активен",
    statusArchived: "В архиве",
    colName: "Клиент",
    colNo: "Номер",
    colTier: "Категория",
    colOwner: "Ответственный",
    colReliability: "Платёжная дисциплина",
    colOpenAr: "Дебиторская задолженность",
    colCredit: "Кредит",
    colCreated: "Создан",
    colUpdated: "Изменён",
    // Целые фразы, а не приклеенное слово направления: по-русски направление
    // согласуется с фразой, а не добавляется в конец английского шаблона.
    sortAsc: "Сортировать по «{{column}}» по возрастанию",
    sortDesc: "Сортировать по «{{column}}» по убыванию",
    provisional: "предварительно",
    arUnavailable: "Нет данных о дебиторской задолженности",
    creditHold: "Кредитный стоп",
    prepayOnly: "Только предоплата",
    currencyWarning:
      "Нет действующего прайс-листа в {{currency}} — расчёт цен не будет работать, пока его не активируют.",
    warningUnknown:
      "У этого клиента есть предупреждение, которое этот экран пока не может назвать.",
    // Без «сначала новые»: порядок теперь выбирает читатель, и его называет
    // aria-sort на заголовке столбца.
    tableCaption: "Клиенты",
    emptyTitle: "Клиентов пока нет",
    emptyBody: "Создайте клиента или загрузите их из мастера настройки.",
    emptyFilteredTitle: "Под эти фильтры ни один клиент не подходит",
    emptyFilteredBody:
      "Расширьте поиск, категорию, статус, ответственного, кредитный стоп, следующий шаг или метку.",
    error: "Не удалось загрузить клиентов",
    colNextStep: "Следующий шаг",
    filterNeedsNextStep: "Нужен следующий шаг",
    filterTag: "Метка",
    anyTag: "Любая метка",
    tagArchivedOption: "{{name}} (в архиве)",
    colTags: "Метки",
    selectAll: "Выбрать клиентов на этой странице",
    selectRow: "Выбрать «{{customer}}»",
    warningPossibleDuplicate:
      "Клиент с похожим названием или тем же БИН/ИИН, возможно, уже есть. Проверьте список клиентов, прежде чем готовить предложение.",
    import: "Загрузить клиентов",
  },

  notes: {
    title: "Заметки",
    pinnedLabel: "Закреплённая заметка",
    pinned: "Закреплена",
    carriedOver: "перенесена",
    byOn: "{{name}}, {{date}}",
    formerMember: "Бывший сотрудник",
    redactedBy: "Текст удалён: {{name}}, {{date}}",
    redactedOn: "Текст удалён {{date}}",
    addLabel: "Новая заметка",
    addHint: "Что должен знать следующий, кто будет работать с клиентом",
    count: "Символов: {{count}} из {{max}}",
    pinOnAdd: "Закрепить заметку",
    add: "Добавить заметку",
    adding: "Добавляем…",
    pin: "Закрепить",
    unpin: "Открепить",
    redact: "Удалить текст",
    redactConfirm:
      "Удалить текст этой заметки для всех? Восстановить его будет нельзя. Автор, дата и закрепление сохранятся.",
    redactYes: "Удалить текст",
    empty: "Заметок пока нет",
    error: "Не удалось загрузить заметки",
    loading: "Загружаем заметки…",
    readOffline: "Нет сети: заметки появятся, когда она вернётся.",
    moreFailed: "Не удалось загрузить более ранние заметки. Нажмите «Показать ещё» снова.",
    writeFailed: "Не удалось сохранить заметку. Ваш текст сохранён в поле.",
    verbFailed: "Не удалось изменить заметку.",
    offline: "Нет сети. Текст сохранён в поле, а кнопка «Добавить» вернётся вместе с сетью.",
    archivedReadOnly: "Клиент в архиве: новые заметки и закрепление недоступны.",
    refusal: {
      CUSTOMER_ARCHIVED: "Клиент в архиве: новые заметки и закрепление недоступны.",
      NOTE_NOT_FOUND: "Эта заметка вам больше не доступна.",
      CUSTOMER_NOT_FOUND: "Этот клиент вам больше не доступен.",
      forbidden: "Ваша роль не может изменять заметки этого клиента.",
    },
  },

  attachments: {
    title: "Прикреплённые файлы",
    showDetached: "Показать откреплённые файлы",
    loading: "Загружаем прикреплённые файлы…",
    error: "Не удалось загрузить прикреплённые файлы",
    readOffline: "Нет сети: прикреплённые файлы появятся, когда она вернётся.",
    empty: "Файлов пока нет.",
    emptyAll: "Сюда ещё не прикрепляли файлов.",
    moreFailed: "Не удалось загрузить более ранние файлы. Нажмите «Показать ещё» снова.",
    withheld:
      "Файлов скрыто от вашей роли: {{count}}. В них коммерческие условия, которые вашей роли не показываются.",
    untitled: "{{type}} без названия",
    byOn: "{{name}}, {{date}}",
    on: "Прикреплён {{date}}",
    detachedChip: "Откреплён",
    download: "Скачать",
    downloading: "Открываем…",
    downloadFailed: "Не удалось открыть файл.",
    detach: "Открепить",
    detaching: "Открепляем…",
    detachFailed: "Не удалось открепить файл.",
    detachTitle: "Открепить этот файл?",
    detachBody:
      "«{{name}}» исчезнет из списка прикреплённых файлов. Файл останется в системе, его можно будет скачать через «Показать откреплённые файлы», но прикрепить заново нельзя.",
    detachYes: "Открепить файл",
    customerArchived: "Клиент в архиве: новые файлы к нему не прикрепляются.",
    col: {
      title: "Файл",
      category: "Категория",
      type: "Тип",
      size: "Размер",
      attached: "Прикреплён",
      actions: "Действия",
    },
    category: {
      technical: "Технический",
      commercial: "Коммерческий",
    },
    categoryHint: {
      technical:
        "Технические файлы, например чертежи и сертификаты, видят все, кто может открыть эту запись, в том числе склад и ОТК.",
      commercial:
        "Коммерческие файлы, например договоры и ценовые соглашения, видят только те, кому показываются цены.",
    },
    problem: {
      type: "Такой тип файла не принимается. Используйте PDF, PNG, JPEG, XLSX, DOCX, PPTX, CSV, TXT или DWG.",
      empty: "Файл пустой, прикреплять нечего.",
      too_large: "Файл больше {{max}}, а это наибольший допустимый размер.",
    },
    upload: {
      choose: "Выбрать файл",
      chooseOther: "Выбрать другой файл",
      types: "PDF, PNG, JPEG, XLSX, DOCX, PPTX, CSV, TXT или DWG, не больше {{max}}.",
      category: "Категория",
      title: "Название (необязательно)",
      titleHint: "Что это за файл, например: Договор поставки 2026",
      titleLong: "Название не длиннее {{max}} символов.",
      hiddenAfter:
        "Ваша роль не видит коммерческие файлы, поэтому после прикрепления этот файл не появится в списке.",
      submit: "Загрузить",
      busy: "Загружаем…",
      progressLabel: "Ход загрузки",
      offline: "Нет сети: кнопка «Загрузить» вернётся вместе с сетью.",
      failed: "Не удалось прикрепить файл.",
      transferFailed:
        "Файл не загрузился до конца. Проверьте сеть и снова нажмите «Загрузить»: загрузка продолжится с того места, где остановилась.",
      done: "Файл прикреплён.",
      phase: {
        hashing: "Готовим файл…",
        creating: "Начинаем загрузку…",
        uploading: "Загружено: {{percent}} %",
        storing: "Проверяем содержимое файла…",
        attaching: "Прикрепляем файл…",
      },
    },
    refusal: {
      FILE_INTEGRITY_MISMATCH:
        "Содержимое файла не соответствует его названию, поэтому файл не прикреплён. Проверьте, тот ли это файл.",
      FILE_CONTENT_TYPE_NOT_ALLOWED:
        "Такой тип файла не принимается, архивы, например ZIP, тоже не принимаются.",
      FILE_TOO_LARGE: "Файл больше наибольшего допустимого размера.",
      FILE_NOT_STORED:
        "Загрузка не завершилась, поэтому файл не прикреплён. Нажмите «Загрузить» снова.",
      ATTACHMENT_FILE_NOT_YOURS: "Прикрепить этот файл может только тот, кто его загрузил.",
      ATTACHMENT_ALREADY_LINKED:
        "Этот файл уже прикреплён. Чтобы прикрепить новую копию, выберите файл снова.",
      ATTACHMENT_FILE_WRONG_PURPOSE:
        "Этот файл загружен для другой цели, поэтому прикрепить его сюда нельзя.",
      ATTACHMENT_NOT_FOUND: "Этот файл вам больше не доступен, список прочитан заново.",
      CUSTOMER_ARCHIVED: "Клиент в архиве: новые файлы к нему не прикрепляются.",
      CUSTOMER_NOT_FOUND: "Этот клиент вам больше не доступен.",
      QUOTE_NOT_FOUND: "Это предложение вам больше не доступно.",
      ORDER_NOT_FOUND: "Этот заказ вам больше не доступен.",
      CUSTOMER_MERGED:
        "Этот клиент объединён с клиентом {{name}}, поэтому файлы теперь прикрепляются там.",
      forbidden: "Ваша роль не может изменять прикреплённые здесь файлы.",
    },
  },

  documentsTab: {
    title: "Документы",
    loading: "Загружаем документы…",
    error: "Не удалось загрузить предложения и заказы клиента",
    readOffline: "Нет сети: документы появятся, когда она вернётся.",
    empty: "У этого клиента пока нет предложений и заказов.",
    moreFailed: "Не удалось загрузить более ранние документы. Нажмите «Показать ещё» снова.",
    col: {
      number: "Номер",
      kind: "Документ",
      status: "Статус",
      total: "Сумма",
      created: "Создан",
      actions: "Действия",
    },
    kind: {
      quote: "Предложение",
      order: "Заказ",
    },
    quotePdf: "Предложение (PDF)",
    proforma: "Счёт на оплату (PDF)",
    shipments: "Отгрузки",
    hideShipments: "Скрыть отгрузки",
    shipmentsTitle: "Отгрузки",
    shipmentsEmpty: "По этому заказу отгрузок пока нет.",
    shipmentsError: "Не удалось загрузить отгрузки заказа.",
  },

  tags: {
    title: "Метки",
    none: "Меток нет",
    archivedSuffix: "в архиве",
    add: "Добавить метку",
    addLabel: "Какую метку добавить",
    choose: "Выберите метку",
    remove: "Снять метку «{{name}}»",
    noneToAdd: "Других активных меток нет",
    optionsFailed: "Не удалось загрузить метки",
    verbFailed: "Не удалось изменить метки клиента.",
    refusal: {
      TAG_ARCHIVED: "Эта метка в архиве, её нельзя добавить.",
      TAG_NOT_FOUND: "Этой метки больше нет.",
      CUSTOMER_ARCHIVED: "Клиент в архиве, его метки не меняются.",
      forbidden: "Ваша роль не может изменять метки этого клиента.",
    },
    cardBody:
      "Метки группируют клиентов для фильтра, массовых действий и выгрузки. Метка в архиве остаётся у своих клиентов.",
    colName: "Метка",
    colStatus: "Статус",
    colActions: "Действия",
    statusActive: "Активна",
    statusArchived: "В архиве",
    newLabel: "Название новой метки",
    new: "Новая метка",
    creating: "Создаём…",
    rename: "Переименовать",
    renameLabel: "Новое название для «{{name}}»",
    archive: "В архив",
    archiving: "Переносим в архив…",
    archiveConfirm:
      "Перенести «{{name}}» в архив? Метка пропадёт из всех списков выбора, и вернуть её в работу будет нельзя. У клиентов она останется.",
    showArchived: "Показать архивные",
    emptyTitle: "Меток пока нет",
    emptyBodyCurator: "Создайте первую, например «Дилер» или «Ключевой клиент».",
    emptyBodyReader: "Метки создаёт администратор на этом экране.",
    emptyActiveTitle: "Активных меток нет",
    emptyActiveBodyCurator:
      "Создайте метку или отметьте «Показать архивные», чтобы увидеть метки в архиве.",
    emptyActiveBodyReader: "Отметьте «Показать архивные», чтобы увидеть метки в архиве.",
    error: "Не удалось загрузить метки",
    refused: "Метки не показываются вашей роли",
    readOnly: "Список меток меняет только администратор.",
    nameTaken: "Активная метка с таким названием уже есть.",
    limitReached: "Активных меток уже 200. Перенесите одну в архив, прежде чем создавать новую.",
    nameInvalid: "Название метки: от 1 до 40 символов, без точки с запятой.",
    saveFailed: "Не удалось сохранить метку.",
    loading: "Загружаем метки…",
    caption: "Метки",
  },

  bulk: {
    selected: "Выбрано: {{count}}",
    clear: "Снять выделение",
    tagLabel: "Метка",
    ownerLabel: "Новый ответственный",
    chooseTag: "Выберите метку",
    chooseOwner: "Выберите ответственного",
    addTag: "Добавить метку",
    removeTag: "Снять метку",
    reassign: "Сменить ответственного",
    archive: "В архив",
    archiveConfirm:
      "Перенести выбранных клиентов в архив (выбрано: {{count}})? Клиенту в архиве нельзя выставить предложение, заказ или сделку.",
    archiveYes: "Перенести в архив",
    liveBusiness:
      "Выбранных клиентов с открытым предложением, заказом или сделкой: {{count}}. Перенос в архив для них будет отклонён.",
    liveBusinessChip: "Есть открытые дела",
    offline: "Нет сети: действия станут доступны, когда она вернётся.",
    working: "Выполняется: {{done}} из {{total}}",
    outcomeDone: "Выполнено: {{count}}",
    already: {
      add_tag: "Метка уже стояла: {{count}}",
      remove_tag: "Метки и так не было: {{count}}",
      reassign_owner: "Уже ответственный: {{count}}",
      archive: "Уже в архиве: {{count}}",
    },
    outcomeRefused: "Отклонено, {{reason}}: {{count}}",
    outcomeFailed: "Не прошло, можно повторить: {{count}}",
    outcomePending: "Не отправлено: {{count}}",
    retryFailed: "Повторить неудачные",
    verb: {
      add_tag: "Добавить метку",
      remove_tag: "Снять метку",
      reassign_owner: "Сменить ответственного",
      archive: "В архив",
    },
    reason: {
      CUSTOMER_ARCHIVED: "клиент в архиве",
      CUSTOMER_HAS_OPEN_DOCS: "есть открытое предложение, заказ или сделка",
      CUSTOMER_NOT_FOUND: "клиент вам недоступен",
      TAG_ARCHIVED: "метка в архиве",
      TAG_NOT_FOUND: "метки больше нет",
      OWNER_CHANGED: "ответственный уже сменился",
      OWNER_NOT_MEMBER: "не действующий сотрудник",
      PERMISSION_DENIED: "вашей роли это недоступно",
      VALIDATION_FAILED: "запрос не принят",
    },
    reasonOther: "код {{code}}",
  },

  create: {
    title: "Новый клиент",
    legalName: "Юридическое наименование",
    displayName: "Рабочее название",
    tier: "Ценовая категория",
    currency: "Валюта",
    currencyHint: "Оставьте пустым — подставится валюта организации",
    taxId: "БИН/ИИН",
    submit: "Создать клиента",
    creating: "Создание…",
    failed: "Не удалось создать клиента.",
  },

  duplicates: {
    title: "Похожие клиенты уже есть в базе",
    checking: "Ищем похожих клиентов…",
    hint: "Клиента всё равно можно создать.",
    open: "Открыть",
    openLabel: "Открыть {{customer}} в новой вкладке",
    matchTaxId: "Тот же БИН/ИИН",
    matchDisplayName: "Похожее название",
    matchLegalName: "Похожее юридическое наименование",
    hidden: "Похожих клиентов, которых вы не можете открыть: {{count}}",
    hidden_one: "{{count}} похожий клиент, которого вы не можете открыть",
    failed: "Не удалось проверить похожих клиентов. Клиента всё равно можно создать.",
  },

  orderImport: {
    open: "Загрузить заказы",
    title: "Загрузка открытых заказов",
    intro:
      "Одна строка на позицию, позиции объединяются по вашему номеру заказа. Заказы загружаются подтверждёнными по ценам из файла, клиенту ничего не отправляется.",
    back: "Назад к заказам",
    allImports: "Все загрузки заказов",
    jobTitle: "Файл загрузки",
    otherKind: "Это не загрузка заказов. Откройте её в мастере настройки.",
    loading: "Загрузка…",
  },

  customerImport: {
    title: "Загрузка клиентов",
    intro: "Каждая строка проверяется до сохранения. Вы решаете, пропускать ли строки с ошибками.",
    back: "Назад к клиентам",
    allImports: "Все загрузки клиентов",
    jobTitle: "Файл загрузки",
    otherKind: "Это не загрузка клиентов. Откройте её в мастере настройки.",
    loading: "Загрузка…",
    roleError: "Не удалось проверить, что ваша роль может делать на этой странице.",
  },

  // Объединение карточек (§4.17), восстановление (§3.14) и карточка-указатель.
  // Карточка в адресе ОСТАЁТСЯ, выбранная ВЛИВАЕТСЯ в неё: экран говорит, какая
  // запись остаётся, а не «победитель» и «проигравший».
  merge: {
    open: "Объединить",
    narrow: "Для объединения клиентов нужен экран шире",
    restore: "Восстановить клиента",
    restoring: "Восстанавливаем…",
    restoreFailed: "Не удалось восстановить клиента.",
    restoreMerged:
      "Этот клиент объединён с карточкой {{name}}, поэтому его нельзя восстановить. Работайте с карточкой {{name}}.",
    anotherCustomer: "другой карточкой",
    tombstoneLead: "Объединён с карточкой",
    tombstoneOn: "· {{date}}",
    listMergedInto: "Объединён с",
    title: "Объединение клиентов",
    intro: "Карточка {{name}} остаётся. Влитый клиент уходит в архив и ссылается сюда.",
    forbidden: "Объединять клиентов могут администраторы группы и площадки.",
    winnerArchived:
      "Этот клиент в архиве, поэтому в него нельзя ничего влить. Сначала восстановите его или откройте карточку, с которой он объединён.",
    winnerError: "Не удалось загрузить карточку клиента",
    pickTitle: "Клиент, которого нужно влить",
    pickLabel: "Найти клиента",
    pickHint: "Название, номер или БИН/ИИН",
    pickChoose: "Выберите клиента",
    pickShort: "Введите хотя бы две буквы для поиска.",
    pickNone: "Другие активные клиенты не найдены.",
    pickFailed: "Не удалось найти клиентов. Попробуйте ещё раз.",
    pickAnother: "Выбрать другого клиента",
    previewLoading: "Считаем, что изменит объединение…",
    previewError: "Не удалось загрузить предпросмотр объединения",
    previewNotFound: "Этот клиент вам недоступен. Выберите другого.",
    previewSame: "Клиента нельзя объединить с самим собой. Выберите другого.",
    stays: "Остаётся",
    mergedIn: "Вливается",
    fieldsTitle: "Какое значение сохранить в каждом поле",
    colField: "Поле",
    keepFrom: "Сохранить «{{field}}» из карточки {{name}}",
    field: {
      legal_name: "Юридическое наименование",
      display_name: "Название",
      tax_id: "БИН/ИИН",
      country: "Страна",
      tier: "Ценовая категория",
      default_currency: "Валюта",
      credit_limit_minor: "Кредитный лимит",
      credit_terms_days: "Отсрочка платежа",
      credit_hold: "Кредитный стоп",
      billing_address: "Юридический адрес",
      shipping_address: "Адрес доставки",
      preferred_channel: "Предпочтительный канал",
      preferred_locale: "Язык общения",
    },
    notRecorded: "Не указано",
    holdOn: "Стоп включён",
    holdOff: "Стоп снят",
    channelNone: "Без предпочтений",
    currencyLinked:
      "У клиентов разные валюты, поэтому валюта и кредитный лимит сохраняются из одной карточки.",
    tierLocked:
      "Ваша роль не может менять ценовую категорию, поэтому сохраняется категория остающейся карточки.",
    taxIdRefused:
      "Этот БИН/ИИН не подходит к выбранной стране. Сохраните другой БИН/ИИН или другую страну и объедините ещё раз.",
    rowsTitle: "Что переносится и что остаётся",
    colKind: "Записи",
    colMoves: "Переносится",
    colStays: "Остаётся",
    colWhy: "Почему остаются",
    kind: {
      customer_contacts: "Контакты",
      contact_channels: "Каналы связи",
      customer_notes: "Заметки",
      customer_tag_links: "Метки",
      quotes: "Коммерческие предложения",
      sales_orders: "Заказы",
      opportunities: "Сделки",
      sales_activities: "Задачи",
      customer_notifications: "Уведомления",
      customer_price_overrides: "Договорные цены",
      customer_distributor_products: "Товары дистрибьютора",
      customer_assignments: "Соисполнители",
      sales_attachments: "Прикреплённые файлы",
      quote_sends: "Записи об отправке КП",
      customer_reliability_snapshots: "Оценки платёжной дисциплины",
    },
    stayWhy: {
      customer_notifications:
        "Отправленные и завершённые уведомления остаются у карточки, от имени которой их отправили. Неотправленные переходят.",
      customer_price_overrides:
        "Они пересекаются с договорной ценой остающейся карточки или выставлены в другой валюте.",
      customer_distributor_products:
        "Остающийся клиент не дистрибьютор или этот товар у него уже есть.",
      customer_reliability_snapshots: "Оценка остаётся у той карточки, по которой её считали.",
    },
    tierRecount:
      "Товары дистрибьютора посчитаны по текущей ценовой категории. При объединении их пересчитают по категории, которую вы сохраните.",
    accessTitle: "Кто получит доступ",
    accessBody:
      "Эти сотрудники работают с влитым клиентом и станут соисполнителями остающейся карточки. Снимите отметку, чтобы не давать доступ.",
    accessNone: "Доступ никто не получит.",
    accessKeep: "Дать доступ: {{name}}",
    filesTitle: "Коммерческие файлы, у которых сменятся читатели",
    filesBody:
      "Эти файлы следуют за своим клиентом, предложением или заказом, поэтому их будут читать те, кто работает с остающейся карточкой.",
    filesNone: "Ни у одного коммерческого файла читатели не сменятся.",
    filesUntitled: "Файл без названия",
    filesWithheld:
      "Коммерческих файлов, у которых сменятся читатели: {{count}}. Их названия вашей роли не показываются.",
    blockersTitle: "Что мешает объединению",
    blocker: {
      MERGE_TAX_ID_CONFLICT:
        "У клиентов разные БИН/ИИН, значит это две разные компании. Сначала исправьте неверный БИН/ИИН в его карточке.",
      MERGE_CURRENCY_MISMATCH:
        "У клиентов разные валюты, а у влитого клиента есть предложения, заказы или сделки в своей валюте. Этих клиентов объединить нельзя.",
      MERGE_IMPORT_ORDER_REF_CONFLICT:
        "У обоих клиентов есть загруженный заказ с одним номером. Сначала отмените по одному из каждой пары:",
    },
    importConflict: "{{ref}}: {{loser}} у вливаемого клиента, {{winner}} у этого",
    blockersHint: "Кнопка объединения недоступна, пока здесь что-то указано.",
    confirm: "Влить в {{name}}",
    confirming: "Объединяем…",
    offline: "Нет связи. Объединить можно будет, когда связь вернётся.",
    staleTitle: "После загрузки предпросмотра что-то изменилось, поэтому он загружен заново:",
    stale: {
      winner: "остающуюся карточку изменили",
      loser: "влитую карточку изменили",
      access: "изменился список тех, кто получит доступ",
      files: "изменились коммерческие файлы",
    },
    staleHint: "Проверьте предпросмотр ниже и объедините ещё раз.",
    busyRetrying: "Карточки были заняты другим изменением. Пробуем ещё раз…",
    busy: "Карточки всё ещё заняты другим изменением. Подождите немного и нажмите «Объединить» снова.",
    failed: "Не удалось объединить клиентов.",
    refused: {
      MERGE_CUSTOMER_ARCHIVED:
        "Один из клиентов ушёл в архив после загрузки предпросмотра. Откройте его, чтобы понять причину, и начните заново.",
      MERGE_SAME_CUSTOMER: "Клиента нельзя объединить с самим собой. Выберите другого.",
      tier: "Ваша роль не может менять ценовую категорию. Сохраните категорию остающейся карточки.",
      notFound: "Один из клиентов вам больше недоступен. Выберите другого.",
      forbidden: "Ваша роль не может объединять клиентов.",
    },
  },

  c360: {
    crumbRoot: "Клиенты",
    error: "Не удалось загрузить карточку клиента",
    creditLimit: "Кредитный лимит",
    creditTerms: "Отсрочка платежа",
    termsDays: "Дней: {{count}}",
    legalName: "Юридическое название",
    preferredLanguage: "Язык общения",
    localeName: {
      en: "английский",
      ru: "русский",
      zh: "китайский",
      fr: "французский",
    },
    creditHold: "Кредитный стоп",
    creditFailed: "Не удалось сохранить изменение кредитных условий.",
    archive: "В архив",
    archiving: "Архивирование…",
    archiveFailed: "Не удалось отправить клиента в архив.",
    formula: "Как считается?",
    reliabilityFormula: "{{onTime}} из {{paid}} счетов вовремя → {{score}}",
    reliabilityNone: "Оценки пока нет",
    tabsAria: "Разделы карточки клиента",
    tabs: {
      overview: "Обзор",
      contacts: "Контакты и каналы",
      pricing: "Цены",
      documents: "Документы",
    },
    overview: {
      arTitle: "Дебиторская задолженность",
      contactTitle: "Кому звонить",
      noPrimary: "Основной контакт не назначен",
      preferredChannel: "Предпочтительный канал",
      owner: "Ответственный",
      quotesTitle: "Последние предложения",
      quotesEmpty: "По этому клиенту предложений пока нет.",
      quotesMore: "Все предложения по клиенту",
      quotesError: "Не удалось загрузить предложения клиента",
      tasksTitle: "Открытые задачи",
      tasksEmpty: "Открытых задач по этому клиенту нет.",
      tasksError: "Не удалось загрузить открытые задачи",
      dealsTitle: "Открытые сделки",
      dealsEmpty: "Открытых сделок по этому клиенту нет.",
      dealsError: "Не удалось загрузить сделки клиента",
      dealsMore: "Все сделки по клиенту",
      ordersTitle: "Последние заказы",
      ordersEmpty: "По этому клиенту заказов пока нет.",
      ordersError: "Не удалось загрузить заказы клиента",
      ordersMore: "Все заказы по клиенту",
    },
    nextStep: "Следующий шаг",
    pricing: {
      effectiveTitle: "Действующий прайс-лист",
      noEffectiveList: "Нет действующего прайс-листа в {{currency}} для этой категории.",
    },
    assignments: {
      title: "Кто ведёт клиента",
      body: "Ответственный и коллеги могут готовить и отправлять предложения этому клиенту.",
      roleOwner: "Ответственный",
      roleCollaborator: "Соисполнитель",
      add: "Добавить коллегу",
      addTitle: "Добавить к клиенту",
      member: "Коллега",
      role: "Роль",
      remove: "Убрать",
      removing: "Убираем…",
      empty: "Пока никто не назначен.",
      lastOwnerGuard:
        "Это единственный ответственный. Сначала назначьте другого — клиент не остаётся без ответственного.",
      ownerSwap:
        "Если сделать ответственным другого сотрудника, нынешний станет соисполнителем и сохранит свои предложения.",
      addFailed: "Не удалось добавить назначение.",
      removeFailed: "Не удалось снять назначение.",
      error: "Не удалось загрузить назначения",
    },
  },

  contacts: {
    add: "Добавить контакт",
    addTitle: "Добавить контакт",
    editTitle: "Изменить контакт",
    fullName: "ФИО",
    position: "Должность",
    primaryLabel: "Основной контакт",
    primary: "Основной",
    primaryYes: "Да",
    primaryNo: "Нет",
    archive: "В архив",
    restore: "Вернуть из архива",
    archived: "В архиве",
    archiveHint:
      "При отправке в архив снимается признак основного контакта и удаляются все его каналы связи.",
    none: "Контакты не заведены.",
    noChannels: "Каналов связи нет",
    channelKind: "Канал",
    identity: "Идентификатор",
    addChannel: "Добавить канал",
    remove: "Удалить",
    verified: "Подтверждён",
    unverified: "Не подтверждён",
    createFailed: "Не удалось добавить контакт.",
    updateFailed: "Не удалось сохранить контакт.",
    channelAddFailed: "Не удалось добавить канал связи.",
    channelRemoveFailed: "Не удалось удалить канал связи.",
  },

  eligible: {
    title: "Товары по дистрибьюторским ценам",
    skuLabel: "Артикул товара",
    skuHint: "Введите артикул целиком",
    skuApplied: "{{sku}} · {{name}}",
    skuNoMatch: "Товара с таким артикулом нет",
    note: "Примечание",
    add: "Добавить товар",
    confirm: "Добавить в список",
    remove: "Убрать",
    colProduct: "Товар",
    colNote: "Примечание",
    colAdded: "Добавлен",
    colActions: "Действия",
    tableCaption: "Товары, на которые дистрибьютор получает свои цены",
    empty: "Список пуст — все предложения считаются по ценам B2B.",
    addFailed: "Не удалось добавить товар.",
    removeFailed: "Не удалось убрать товар.",
  },

  // §2.9 — «индивидуальная цена»: то, о чём менеджер договорился с одним
  // клиентом по одной позиции. «Отклонение от прайса» — слово для внутренней
  // логики; на экране клиент читает про свою цену, а не про отклонение.
  overrides: {
    title: "Индивидуальные цены",
    intro: "Договорные цены действуют после одобрения и до окончания срока.",
    tableCaption: "Индивидуальные цены этого клиента",
    filterStatus: "Статус",
    filterAll: "Все статусы",
    status: {
      pending_approval: "На согласовании",
      active: "Действует",
      expired: "Истекла",
      revoked: "Отозвана",
    },
    kind: {
      fixed_price: "Фиксированная цена",
      discount_pct: "Скидка",
    },
    approver: {
      facility_admin: "администратора площадки",
      group_admin: "администратора группы",
    },
    roleName: {
      facility_admin: "администратор площадки",
      group_admin: "администратор группы",
    },
    colProduct: "Позиция",
    colValue: "Цена",
    colMinQty: "От количества",
    colValidity: "Срок действия",
    colStatus: "Статус",
    colReason: "Обоснование",
    colActions: "Действия",
    perUnit: "{{price}} за {{uom}}",
    discountValue: "скидка {{pct}}% от цены категории",
    redacted: "Вашей роли не показывается",
    anyQty: "Любое количество",
    fromQty: "от {{qty}} {{uom}}",
    fromQtyNoUom: "от {{qty}}",
    needs: "Нужно согласование {{approver}}",
    reviewDelta: "Ниже порога на {{amount}} за единицу",
    propose: "Предложить цену",
    proposeTitle: "Предложить индивидуальную цену",
    proposing: "Отправляем…",
    kindLabel: "Вид",
    value: "Цена за единицу",
    currencyFact: "В {{currency}} — валюте клиента",
    currencyFactUom: "В {{currency}} — валюте клиента, за {{uom}}",
    pct: "Скидка, %",
    pctHint: "От цены прайс-листа категории",
    minQty: "Применяется от количества",
    minQtyHint: "В базовой единице позиции; пусто — при любом количестве",
    minQtyHintUom: "В {{uom}}; пусто — при любом количестве",
    validFrom: "Действует с",
    validTo: "Действует по",
    validToHint: "Пусто — бессрочно",
    reason: "Обоснование",
    reasonHint: "Обязательно — это довод для согласующего и запись в журнале",
    validityOrder: "Дата окончания не может быть раньше даты начала.",
    notListed:
      "{{code}}: ни один прайс-лист категории не содержит позицию на дату начала. Сначала добавьте её в прайс-лист.",
    createFailed: "Не удалось предложить цену.",
    outcomeActive: "Действует — следующее предложение этому клиенту будет рассчитано по ней.",
    outcomePending:
      "На согласовании у {{approver}} — порог маржи не пройден. Не применяется, пока не одобрят.",
    outcomePendingDelta:
      "На согласовании у {{approver}} — ниже порога на {{amount}} за единицу. Не применяется, пока не одобрят.",
    outcomePendingMargin:
      "На согласовании у {{approver}} — {{margin}}% при пороге {{guard}}%. Не применяется, пока не одобрят.",
    outcomePendingBreach:
      "На согласовании у {{approver}} — ниже порога на {{amount}} за единицу ({{margin}}% при пороге {{guard}}%). Не применяется, пока не одобрят.",
    revoke: "Отозвать",
    revokeReason: "Причина",
    revokeConfirm: "Подтвердить отзыв",
    revoking: "Отзываем…",
    revokeNote:
      "Уже отправленные предложения сохраняют цену, с которой ушли; черновики пересчитаются при следующем изменении.",
    revokeRoleNote: "Отозвать индивидуальную цену может администратор площадки или группы.",
    revokeFailed: "Не удалось отозвать цену.",
    revokeDenied: "Отзыв доступен ролям: {{roles}}. Обратитесь к одной из них.",
    empty: "У этого клиента пока нет индивидуальных цен.",
    emptyFiltered: "Индивидуальных цен в этом статусе нет.",
    error: "Не удалось загрузить индивидуальные цены.",
  },

  priceCheck: {
    title: "Проверка цены",
    product: "Артикул товара",
    productHint: "Введите артикул целиком",
    productApplied: "{{sku}} · {{name}}",
    productNoMatch: "Товара с таким артикулом нет",
    qty: "Количество",
    uom: "Единица",
    uomPick: "Выберите единицу",
    date: "Желаемая дата",
    resolve: "Рассчитать цену",
    resolving: "Расчёт…",
    price: "Цена за единицу",
    listPrice: "Цена по прайсу",
    source: {
      tier_list: "Прайс категории",
      customer_override: "Индивидуальная цена",
      offcut_class: "Скидка на обрезки",
      manual: "Ручная цена",
    },
    fallbackChip: "b2b (нет в списке дистрибьютора)",
    needsApproval: "Нужно согласование",
    marginPct: "Маржа {{pct}}%",
    unitCost: "Себестоимость {{cost}}",
    guardPct: "Порог {{pct}}%",
    notListed: "Для этого товара, количества и даты цены в прайсе нет — нужна ручная цена.",
    failed: "Не удалось рассчитать цену.",
    valueHidden: "Цены не показываются для вашей роли, поэтому проверка цены недоступна.",
  },

  priceLists: {
    title: "Прайс-листы",
    new: "Новый прайс-лист",
    filterTier: "Категория",
    filterCurrency: "Валюта",
    filterStatus: "Статус",
    anyTier: "Любая категория",
    anyStatus: "Любой статус",
    currencyHint: "например, KZT",
    colCode: "Код",
    colName: "Название",
    colTier: "Категория",
    colCurrency: "Валюта",
    colVat: "НДС",
    colValidity: "Действует",
    colStatus: "Статус",
    colActivated: "Активирован",
    openEnded: "бессрочно",
    notActivated: "Не активирован",
    status: {
      draft: "Черновик",
      active: "Действует",
      archived: "В архиве",
    },
    vat: {
      inclusive: "С НДС",
      exclusive: "Без НДС",
    },
    tableCaption: "Прайс-листы, сначала новые",
    emptyTitle: "Прайс-листов пока нет",
    emptyBody: "Создайте черновик для категории и валюты, затем активируйте его.",
    error: "Не удалось загрузить прайс-листы",
  },

  listCreate: {
    title: "Новый прайс-лист",
    code: "Код",
    name: "Название",
    tier: "Категория",
    currency: "Валюта",
    vatMode: "Режим НДС",
    vatModeDefault: "По умолчанию для категории",
    validFrom: "Действует с",
    validTo: "Действует по",
    submit: "Создать черновик",
    failed: "Не удалось создать прайс-лист.",
  },

  priceListDetail: {
    crumbRoot: "Прайс-листы",
    error: "Не удалось загрузить прайс-лист",
    activate: "Активировать",
    activating: "Активация…",
    activateFailed: "Не удалось активировать прайс-лист.",
    archive: "В архив",
    archiveFailed: "Не удалось отправить прайс-лист в архив.",
    readOnlyNote:
      "Редактировать можно только черновик. Чтобы изменить цены, создайте новый черновик.",
    wholesaleNote:
      "Сохранение заменяет весь набор строк: удалённые здесь строки удаляются из прайса.",
    colProduct: "Товар",
    colUom: "Единица",
    colMinQty: "От количества",
    colPrice: "Цена за единицу",
    colMargin: "Ожидаемая маржа",
    colActions: "Действия",
    marginPct: "{{pct}}%",
    addSku: "Артикул товара",
    addUom: "Единица",
    addMinQty: "От количества",
    addPrice: "Цена за единицу",
    addRow: "Добавить строку",
    removeRow: "Удалить",
    skuApplied: "{{sku}} · {{name}}",
    skuNoMatch: "Товара с таким артикулом нет",
    save: "Сохранить строки",
    saveFailed: "Не удалось сохранить строки.",
    emptyItems: "Строк пока нет — добавьте их вручную или загрузите файл.",
    tableCaption: "Строки прайс-листа",
    reportTitle: "Результат загрузки",
    reportApplied: "Принято строк: {{count}}",
    reportDeleted: "Удалено строк: {{count}}",
    reportClean: "Все строки приняты.",
    rowCode: {
      PRODUCT_NOT_FOUND: "Товара с таким артикулом нет",
      UOM_NOT_FOUND: "Такой единицы измерения нет",
      UOM_NOT_CONVERTIBLE: "Эта единица не переводится в базовую единицу товара",
      DUPLICATE_BREAK: "Две строки задают один товар, единицу и порог количества",
      PRICE_INVALID: "Цена записана в виде, который эта валюта не может передать",
      MIN_QTY_INVALID: "Порог количества записан не числом",
      ROW_INVALID: "В строке нет артикула, единицы или цены",
    },
    conflictTitle: "Другой действующий прайс-лист уже покрывает эти даты",
    conflictBody:
      "На одну категорию и валюту может действовать только один прайс-лист. Измените даты этого листа или отправьте конфликтующий в архив.",
    conflictOwn: "Этот лист",
  },

  import: {
    title: "Загрузка цен",
    hint: "CSV или TSV со столбцами sku, uom, min_qty, price. Вставка из таблицы тоже работает.",
    file: "Выберите файл",
    paste: "Или вставьте строки",
    pasteHint: "sku\tuom\tmin_qty\tprice",
    run: "Загрузить",
    running: "Загрузка…",
    lineN: "Строка {{n}}",
    badHeader: "В первой строке должны быть названы столбцы sku, uom и price.",
    emptyFile: "Под заголовком нет ни одной строки.",
  },

  offcut: {
    title: "Скидки на обрезки",
    new: "Новый класс скидки",
    createTitle: "Новый класс скидки",
    editTitle: "Изменить класс скидки",
    code: "Код",
    codeHint: "ЗАГЛАВНЫЕ_С_ПОДЧЁРКИВАНИЕМ, например OFFCUT_COMBO3",
    nameIn: "Название ({{language}})",
    minPieces: "Кусков от",
    maxPieces: "Кусков до",
    minLength: "Длина куска от",
    maxLength: "Длина куска до",
    discount: "Скидка, %",
    bypassLabel: "Обходит порог маржи",
    bypassHint: "Класс, который обходит порог, даёт цену ниже минимальной маржи без согласования.",
    archivedLabel: "В архиве",
    colCode: "Код",
    colName: "Название",
    colPieces: "Кусков",
    colMinLength: "Длина от",
    colMaxLength: "Длина до",
    colDiscount: "Скидка",
    colBypass: "Порог маржи",
    colStatus: "Статус",
    colActions: "Действия",
    pct: "{{pct}}%",
    bypasses: "Обходит",
    guarded: "Учитывает",
    gaNote: "Изменять классы скидок может только администратор группы.",
    readOnly: "Классы скидок настраивает администратор группы.",
    tableCaption: "Классы скидок на обрезки",
    emptyTitle: "Классов скидок нет",
    emptyBody:
      "Отраслевой пакет заводит OFFCUT_SINGLE и OFFCUT_COMBO2 — установите пакет или создайте класс здесь.",
    error: "Не удалось загрузить классы скидок",
    createFailed: "Не удалось создать класс скидки.",
    updateFailed: "Не удалось сохранить класс скидки.",
    ratesHidden:
      "Размеры скидок не показываются для вашей роли, поэтому существующие классы здесь не изменить.",
  },

  guards: {
    title: "Пороги маржи",
    new: "Новый порог маржи",
    createTitle: "Новый порог маржи",
    scopeLabel: "Применяется к",
    scope: {
      default: "Всем товарам",
      product: "Одному товару",
      family: "Группе товаров",
    },
    basis: {
      standard: "Плановая себестоимость",
      moving_avg: "Скользящая средняя себестоимость",
    },
    tenantDefault: "Значение по организации",
    familyTarget: "Группа товаров",
    product: "Артикул товара",
    productHint: "Введите артикул целиком",
    productApplied: "{{sku}} · {{name}}",
    productNoMatch: "Товара с таким артикулом нет",
    minMargin: "Минимальная маржа, %",
    pct: "{{pct}}%",
    gaNote: "Изменять ограничения маржи может только администратор группы.",
    archive: "В архив",
    restore: "Вернуть из архива",
    colScope: "Применяется к",
    colTarget: "Объект",
    colMinMargin: "Минимальная маржа",
    colCostBasis: "База себестоимости",
    colStatus: "Статус",
    colActions: "Действия",
    tableCaption: "Пороги маржи",
    emptyTitle: "Порогов маржи нет",
    emptyBody:
      "Без порога ни одна цена не помечается как нарушение. Задайте сначала порог по всей организации.",
    error: "Не удалось загрузить пороги маржи",
    createFailed: "Не удалось создать порог маржи.",
    updateFailed: "Не удалось сохранить порог маржи.",
  },

  quoteStatus: {
    draft: "Черновик",
    pending_approval: "На согласовании",
    approved: "Согласовано",
    sent: "Отправлено",
    accepted: "Принято",
    converted: "Оформлен заказ",
    rejected: "Отклонено",
    expired: "Срок истёк",
    cancelled: "Отменено",
  },

  priceSource: {
    tier_list: "Цена категории",
    customer_override: "Индивидуальная цена",
    offcut_class: "Обрезки −{{pct}}%",
    offcutPlain: "Скидка на обрезки",
    manual: "Введено вручную",
    offList: "Вне прайса",
  },

  atp: {
    label: "Наличие",
    green: "Есть на складе",
    amber: "Частично на складе",
    red: "Под производство",
    unknown: "Не проверялось",
  },

  quotes: {
    title: "Предложения",
    new: "Новое предложение",
    tableCaption: "Предложения",
    colNo: "Предложение",
    colCustomer: "Клиент",
    colTotal: "Сумма",
    colStatus: "Статус",
    colViewed: "Открыто",
    colValid: "Действует до",
    colOrigin: "Источник",
    colRevisions: "Версия",
    revision: "в.{{count}}",
    pdf: "Предложение (PDF)",
    origin: {
      manual: "Вручную",
      agent: "Агент",
      portal: "Портал",
    },
    viewedYes: "Открыто",
    viewedNo: "Не открыто",
    viewedOn: "Открыто {{when}}",
    validDays: "Осталось дней: {{count}}",
    validExpired: "Срок вышел",
    filterStatus: "Статус",
    filterOwner: "Ответственный",
    filterCustomer: "Клиент",
    filterOrigin: "Источник",
    filterOffList: "Только строки вне прайса",
    filterFrom: "Создано с",
    filterTo: "Создано по",
    filterAll: "Все",
    filterCustomerHint: "Название или номер клиента",
    sortAsc: "Сортировать по «{{column}}», по возрастанию",
    sortDesc: "Сортировать по «{{column}}», по убыванию",
    colCreated: "Создано",
    selectAll: "Выбрать предложения на этой странице",
    selectRow: "Выбрать {{quote}}",
    bulkSelected: "Выбрано: {{count}}",
    bulkClear: "Снять выделение",
    bulkRemind: "Отправить напоминание",
    bulkExpire: "Закрыть по сроку",
    bulkWorking: "Выполняем…",
    bulkSentOnly: "Напоминания и закрытие по сроку возможны только для отправленных предложений.",
    bulkResult: "Выполнено: {{done}}. Отклонено: {{failed}}.",
    bulkRemindFailed: "Не удалось отправить все напоминания.",
    bulkExpireFailed: "Не удалось закрыть по сроку все предложения.",
    emptyTitle: "Предложений пока нет",
    emptyBody: "Посчитайте запрос клиента — предложение появится здесь.",
    emptyFilteredTitle: "Под эти фильтры ничего не подходит",
    emptyFilteredBody: "Расширьте или сбросьте эти фильтры.",
    error: "Не удалось загрузить предложения",
    loading: "Загружаем предложения…",
  },

  composer: {
    crumbRoot: "Предложения",
    newTitle: "Новое предложение",
    editTitle: "Предложение {{no}}",
    customer: "Клиент",
    customerHint: "Название или номер клиента",
    customerRequired: "Сначала выберите клиента.",
    contact: "Получатель",
    contactNone: "Контакт не выбран",
    validity: "Срок действия, дней",
    noteToCustomer: "Комментарий для клиента",
    internalNote: "Внутренний комментарий",
    internalNoteHint: "Клиент его не видит.",
    linesTitle: "Строки",
    addLine: "Добавить строку",
    product: "Продукт",
    productHint: "Точный артикул",
    productNoMatch: "Продукта с таким артикулом нет",
    qty: "Количество",
    uom: "Единица",
    colProduct: "Продукт",
    colQty: "Количество",
    colUnitPrice: "Цена за единицу",
    colPromise: "Обещанная дата",
    promiseDate: "Обещанная дата по строке",
    promisePast: "Обещанная дата не может быть раньше сегодняшней.",
    promiseNone: "Не указана",
    colDelivery: "Поставка",
    checkDate: "Проверить дату",
    deliveryBy: "Поставка до {{date}}",
    deliveryQuoted: "Предложено",
    deliveryRemove: "Убрать дату",
    deliveryPriceReset: "Единица изменилась, поэтому введённая цена сброшена.",
    checkDateProductError: "Не удалось загрузить товар для проверки даты",
    deliveryRefused: {
      not_found: "Этой даты больше нет. Проверьте дату заново или уберите её.",
      not_quoted: "Эта дата больше не предложена. Проверьте дату заново или уберите её.",
      expired: "Срок этой даты истёк. Проверьте дату заново или уберите её.",
      product_mismatch:
        "Эта дата проверялась для другого товара. Проверьте дату заново или уберите её.",
      qty_mismatch:
        "Эта дата проверялась для другого количества. Проверьте дату заново или уберите её.",
    },
    colSource: "Источник",
    colAtp: "Наличие",
    colMargin: "Маржа",
    colTotal: "Сумма строки",
    colActions: "Действия",
    removeLine: "Убрать",
    listPrice: "По прайсу {{price}}",
    priceTyped: "Введите цену",
    priceResolved: "По расчёту {{price}}",
    linesIncomplete:
      "Строк с незаполненным количеством или ценой: {{count}} \u2014 сохранить можно после исправления.",
    lineDefectQty: "{{product}} (строка {{line}}): количество не число.",
    lineDefectPrice: "{{product}} (строка {{line}}): цена не число.",
    lineDefectQtyNoProduct: "Строка {{line}}: количество не число.",
    lineDefectPriceNoProduct: "Строка {{line}}: цена не число.",
    totalsIncomplete: "Строк без цены: {{count}} \u2014 в этой сумме учтены только строки с ценой.",
    resolving: "Считаем…",
    resolveFailed: "Не удалось рассчитать цену строки.",
    notListed: "Для этого продукта нет цены в прайсе клиента — введите цену вручную.",
    priceHidden:
      "Цены не показываются для вашей роли, поэтому у этой строки нет предварительной цены.",
    totalsHidden: "Итоги не показываются для вашей роли.",
    reviseValueHidden:
      "При сохранении договорные цены заменятся ценами из прайса, которые вам не видны. Попросите сохранить сотрудника, которому видны цены.",
    marginTitle: "Маржа",
    marginPct: "{{pct}}%",
    marginGuard: "Порог {{pct}}%",
    marginBreached: "Ниже порога",
    needsApproval: "Нужно согласование",
    subtotal: "Сумма без НДС",
    tax: "НДС",
    total: "Итого",
    vatInclusive: "Цены с НДС",
    vatExclusive: "НДС начисляется сверху",
    totalsPreview: "Итоги пересчитываются при сохранении черновика.",
    vatOnSave: "НДС начисляется при сохранении черновика.",
    saveDraft: "Сохранить черновик",
    saving: "Сохраняем…",
    send: "Отправить",
    sending: "Отправляем…",
    sendForApproval: "Отправить на согласование",
    sendApprovalHint: "Одна из строк ниже порога маржи, поэтому отправка сначала уйдёт на решение.",
    newOrderTitle: "Новый заказ",
    facility: "Отгружать с площадки",
    facilityDerived: "Выбрать автоматически",
    facilityDerivedNote: "Заказ будет отгружен с площадки, где хранится его запас.",
    poRef: "Номер заказа клиента",
    placeOrder: "Оформить заказ",
    placeOrderForApproval: "Оформить на согласование",
    placing: "Оформляем…",
    orderApprovalHint:
      "Строка ниже порога маржи или не по прайсу — заказ подтвердится после согласования.",
    orderFailed: "Не удалось оформить заказ.",
    orderMobileNote: "Оформить заказ можно только с большого экрана.",
    totalsPreviewOrder: "Итоги пересчитываются при оформлении заказа.",
    vatOnPlace: "НДС начисляется при оформлении заказа.",
    duplicate: "Дублировать",
    duplicating: "Дублируем…",
    duplicateHint: "Копирует строки в новый черновик и пересчитывает их по действующим прайсам.",
    revise: "Сохранить как новую версию",
    reviseHint:
      "Предложение уже отправлено. Сохранение создаст версию {{next}}, и прежняя ссылка клиента перестанет работать.",
    saveFailed: "Не удалось сохранить предложение.",
    sendFailed: "Не удалось отправить предложение.",
    sendGlyphsSource:
      "Эти символы есть в самом предложении или в колонтитулах документов в настройках продаж. Замените их и отправьте предложение снова.",
    duplicateFailed: "Не удалось продублировать предложение.",
    sentTitle: "Отправлено",
    sentBody: "Клиент может принять его по ссылке.",
    pendingTitle: "Отправлено на согласование",
    pendingBody: "Уйдёт клиенту сразу после решения по марже.",
    offcutTitle: "Вариант из обрезков",
    offcutOpen: "Варианты из обрезков",
    offcutClose: "Закрыть",
    offcutOption: "{{qty}} как {{pieces}} со скидкой {{pct}}%",
    offcutWhere: "Зона {{location}}",
    offcutApply: "Взять этот",
    offcutApplied: "Вариант из обрезков применён",
    offcutNone: "Нет комбинации обрезков на такое количество.",
    offcutValueHidden:
      "Цены не показываются для вашей роли, поэтому варианты из обрезков не предлагаются.",
    offcutUndiscounted:
      "Обрезки покрывают это количество, но скидка на обрезки к ним не применяется.",
    offcutFailed: "Не удалось получить варианты из обрезков.",
    offcutSaves: "Используются уже отрезанные остатки вместо нового кабеля.",
    agentTitle: "Черновик подготовил агент",
    agentBody: "Проверьте, поправьте что нужно и отправьте сами.",
    agentSource: "Клиент написал:",
    agentNoSource: "Исходное сообщение недоступно.",
    agentOrigin: "Черновик агента",
    agentNote: "Комментарий агента",
    agentNoteFrame: "Написано агентом. Проверьте перед отправкой.",
    mobileNote:
      "Редактирование строк требует большего экрана. Здесь можно прочитать, согласовать и отправить.",
    collaboratorsTitle: "Кто может работать с предложением",
    collaboratorsOwner: "Ответственный по клиенту",
    collaboratorsWithLabel: "Вместе с",
    collaboratorsManage: "Изменить в карточке клиента",
    emptyLinesTitle: "Строк пока нет",
    emptyLinesBody: "Добавьте первый продукт и количество.",
    error: "Не удалось загрузить предложение",
    readOnly: "Это предложение больше нельзя редактировать.",
    expiredNote:
      "Срок предложения истёк. Сохраните новую версию — она пересчитается по текущим прайс-листам, и её можно отправить снова.",
    expiredNoteBlocked: "Срок предложения истёк, отправить его больше нельзя.",
    customerArchivedNote:
      "Клиент в архиве, поэтому предложение нельзя изменить или скопировать. Создайте новое предложение.",
    customerUnassignedNote:
      "Клиент больше не закреплён за вами, поэтому предложение нельзя изменить или скопировать. Попросите администратора закрепить его.",
    linkedCustomerArchived:
      "Клиент, на которого ведёт эта ссылка, находится в архиве, поэтому новый документ на него оформить нельзя. Выберите другого клиента.",
    diffTitle: "Скопировано из {{no}}",
    diffNone: "Все строки сохранили свои цены, ничего не пропущено.",
    diffRepriced: "Строк пересчитано: {{count}}.",
    diffDropped: "Строк пропущено: {{count}}.",
    diffDroppedLine: "Строка {{line}} — {{product}}",
    diffDroppedWhy:
      "Этих продуктов нет в прайс-листе клиента, поэтому они не перенесены. Добавьте их заново, указав цену вручную.",
    versionsTitle: "Версии",
    versionsShow: "Показать версии",
    versionsHide: "Скрыть версии",
    versionsBody: "Что было в предыдущих версиях этого предложения.",
    versionsError: "Не удалось загрузить версии",
    versionLines: "Строк: {{count}}",
    notificationsTitle: "Что сообщили клиенту",
    notificationsEmpty: "По этому предложению клиенту ещё ничего не отправляли.",
    notificationsError: "Не удалось загрузить сообщения клиенту",
    cancelQuote: "Отменить предложение",
    cancelReason: "Причина отмены",
    cancelReasonHint: "Сохраняется в предложении и в журнале аудита.",
    cancelConfirm: "Отменить",
    cancelKeep: "Оставить",
    cancelling: "Отменяем…",
    cancelWarning: "Ссылка клиента перестанет работать сразу после этого.",
    cancelFailed: "Не удалось отменить предложение.",
    recordRejection: "Записать отказ",
    rejectReason: "Причина",
    rejectReasonRequired: "Выберите причину",
    rejectReasonPrice: "Цена",
    rejectReasonLeadTime: "Срок поставки",
    rejectReasonStock: "Наличие",
    rejectReasonOther: "Другое",
    rejectNote: "Комментарий",
    rejectNoteHint: "Что сказал клиент.",
    rejectConfirm: "Записать",
    rejectKeep: "Не записывать",
    rejectRecording: "Записываем…",
    rejectFailed: "Не удалось записать отказ.",
    rejectedBecause: "Причина отказа клиента:",
    cancelledBecause: "Причина отмены:",
    convertedTo: "Стало заказом",
    deal: "Сделка",
    dealNone: "Без сделки",
    dealHidden: "В сделке, которая вам недоступна",
    dealChip: "{{no}} · {{title}}",
    dealLink: "Привязать к сделке",
    dealLinkTitle: "Привязать предложение к сделке",
    dealLinkHint: "Открытые сделки этого клиента.",
    dealLinkEmpty: "У этого клиента нет открытых сделок.",
    dealLinkNoneWritable:
      "У этого клиента нет открытых сделок, к которым вы можете привязать это предложение.",
    dealLinkNoneYet:
      "Среди загруженных сделок нет таких, к которым можно привязать предложение. «Показать ещё» загрузит следующие.",
    dealLinkConfirm: "Привязать",
    dealLinking: "Привязываем…",
    dealUnlink: "Отвязать",
    dealUnlinking: "Отвязываем…",
    dealLinkFailed: "Не удалось изменить сделку предложения.",
    dealFromPage: "Предложение будет относиться к сделке, из которой его начали.",
  },

  orders: {
    title: "Заказы",
    tableCaption: "Заказы",
    colNo: "Заказ",
    colCustomer: "Клиент",
    colStatus: "Статус",
    colTotal: "Сумма",
    colOwner: "Ответственный",
    colCredit: "Кредит",
    colConfirmed: "Подтверждён",
    colPromise: "Обещанная дата",
    colPo: "Номер заказа клиента",
    filterStatus: "Статус",
    filterAll: "Все",
    filterCustomer: "Клиент",
    filterCustomerHint: "Название или номер клиента",
    filterFacility: "Площадка",
    filterAtRisk: "Только под угрозой",
    atRiskReason: {
      promise_broken: "Обещание нарушено",
      promise_at_risk: "Обещание под угрозой",
      date_passed: "Срок прошёл",
    },
    atRiskEmptyTitle: "Заказов под угрозой нет",
    atRiskEmptyBody: "Все открытые заказы идут к своей дате.",
    filterImport: "Загружены из",
    filterImportClear: "Показать все заказы",
    importRefusedTitle: "Эти заказы вам не показываются",
    importRefusedBody:
      "Список заказов этой загрузки видят только тот, кто её выполнил, и администратор.",
    emptyFilteredTitle: "Под эти фильтры ничего не подходит",
    emptyFilteredBody: "Расширьте статус, клиента или площадку.",
    sortAsc: "Сортировать по «{{column}}», по возрастанию",
    sortDesc: "Сортировать по «{{column}}», по убыванию",
    emptyTitle: "Заказов пока нет",
    emptyBody:
      "Заказов пока нет — они появляются, когда клиент принимает предложение или менеджер оформляет заказ.",
    new: "Новый заказ",
    error: "Не удалось загрузить заказы",
    loading: "Загружаем заказы…",
    status: {
      pending_approval: "На согласовании",
      confirmed: "Подтверждён",
      in_production: "В производстве",
      ready: "Готов",
      shipped: "Отгружен",
      delivered: "Доставлен",
      closed: "Закрыт",
      cancelled: "Отменён",
    },
    credit: {
      passed: "Кредит в норме",
      blocked_credit: "Превышен кредитный лимит",
      blocked_prepay: "Нужна предоплата",
      override_approved: "Стоп снят",
    },
    detail: {
      crumbRoot: "Заказы",
      error: "Не удалось загрузить заказ",
      timeline: "Где сейчас заказ",
      promised: "Обещано",
      promiseStatus: {
        committed: "Подтверждено",
        at_risk: "Под угрозой",
        broken: "Нарушено",
        kept: "Выполнено",
      },
      promiseChecking: "Дата поставки проверяется",
      confirmedNoticePending: "Уйдёт вместе с датой поставки",
      checkDateLine: "Проверить дату для строки {{line}}",
      rePromiseLine: "Перенести обещание по строке {{line}}",
      promiseProjected: "теперь {{date}}",
      promiseRisk: {
        stock_consumed: "Остатки ушли на другое",
        material_delay: "Материал опаздывает",
        capacity_loss: "Потеря мощности",
        schedule_slip: "График сдвинулся",
      },
      lineLabel: "{{order}} · строка {{line}}",
      linesTitle: "Строки",
      colProduct: "Продукт",
      colQty: "Кол-во",
      colShipped: "Отгружено",
      colPrice: "Цена",
      colTotal: "Сумма",
      colMode: "Обеспечение",
      colReservations: "Резерв",
      colLineStatus: "Статус",
      mode: {
        stock: "Со склада",
        offcut_combo: "Комбинация обрезков",
        production: "Под производство",
        mixed: "Смешанное",
      },
      lineStatus: {
        open: "Открыта",
        in_production: "В производстве",
        ready: "Готова",
        shipped: "Отгружена",
        cancelled: "Отменена",
      },
      reservation: "Единица {{n}}",
      noReservations: "Резерва нет — строка идёт под производство.",
      reservationsReleased: "Резерв снят при отмене строки.",
      notificationsTitle: "Что сообщили клиенту",
      notifEmpty: "По этому заказу сообщений ещё не было.",
      degradedTitle: "Обеспечение изменилось после принятия",
      degradedLine: "Строка {{line}}: планировалось {{planned}}, теперь {{actual}}",
      degradedImpact: "Разница по скидке: {{amount}}",
      trackingTitle: "Ссылка для клиента",
      trackingCopy: "Скопировать ссылку",
      trackingCopied: "Скопировано",
      trackingHint:
        "Клиент следит за этим заказом на отдельной странице. Скопируйте ссылку и отправьте её клиенту.",
      trackingOpen: "Открыть страницу",
      cancel: "Отменить заказ",
      cancelReason: "Причина отмены",
      cancelConfirm: "Отменить",
      cancelKeep: "Оставить",
      cancelling: "Отменяем…",
      cancelFailed: "Не удалось отменить заказ.",
      colActions: "Действия",
      lineCancel: "Отменить строку",
      lineCancelTarget: "Отмена строки {{n}} — {{product}}",
      lineCancelReason: "Причина отмены строки",
      lineCancelConfirm: "Отменить строку",
      lineCancelKeep: "Оставить",
      lineCancelling: "Отменяем…",
      lineCancelFailed: "Не удалось отменить строку.",
      lineCancelLast: "Это последняя открытая строка — её отмена отменит весь заказ.",
      lineCancelLastShipped:
        "Это последняя открытая строка — её отмена закроет заказ как отгруженный.",
      poRef: "Номер заказа клиента",
      imported: "Загружен из файла открытых заказов",
      quoteLink: "Из предложения {{no}}",
      reviewPending: "Ждёт решения: {{approver}}",
      approver: {
        facility_admin: "администратор площадки",
        group_admin: "администратор группы",
      },
      openReviews: "Открыть согласования",
      cancelAdminOnly: "Отозвать заказ на согласовании может только администратор.",
      cancelBlockedByShipments:
        "Отменить этот заказ здесь уже нельзя. Сначала отмените открытую отгрузку; отгруженный товар возвращается через возврат.",
      lineNeedsApproval: "Требует согласования",
      lineOffList: "Не по прайсу",
      colListPrice: "По прайсу",
      colMargin: "Маржа",
      shipmentsTitle: "Отгрузки",
      colShipmentNo: "Отгрузка",
      colShipmentStatus: "Статус",
      colShipmentLines: "Строк",
      colCarrier: "Перевозчик",
      colShippedAt: "Отгружено",
      colDeliveredAt: "Доставлено",
      openShipQueue: "Открыть в очереди отгрузки",
      markDelivered: "Отметить доставленным",
      deliverTitle: "Подтвердить доставку по данным перевозчика",
      deliverConfirm: "Да, доставлено",
      deliverKeep: "Пока нет",
      deliverFailed: "Не удалось отметить заказ доставленным.",
      progressOf: "{{shipped}} из {{ordered}}",
      promisePending: "Дату обещания ставит планирование.",
      colPromise: "Обещанная дата",
      colProduction: "Производство",
      woMade: "Выпущено {{good}} из {{planned}} {{uom}}",
      noWorkOrders: "Заказа на производство пока нет.",
      planWorkOrder: "Запланировать заказ на производство",
      cancelBlockedByWos:
        "Пока строка в производстве, заказ клиента отменить нельзя. Сначала отмените его заказы на производство в разделе «Производство».",
      proforma: "Счёт на оплату (PDF)",
      duplicatePo:
        "У этого клиента уже есть открытый заказ с номером заказа клиента {{po}}. Проверьте, что это не один и тот же заказ, оформленный дважды.",
      duplicatePoLink: "Заказы этого клиента",
      warningUnknown:
        "Заказ создан, но с предупреждением, которое этот экран пока не может назвать.",
      requote: "Новое предложение по заказу",
      requoting: "Готовим предложение…",
      requoteFailed: "Не удалось создать предложение по этому заказу.",
      requoteNoLines:
        "Ни у одного продукта из этого заказа нет цены в действующих прайс-листах, поэтому предложение не создано.",
      colDeliveryNote: "Накладная",
      override: "Изменить статус вручную",
      overrideTitle: "Перевести заказ дальше вручную",
      overrideTarget: "Новый статус",
      overrideBody: {
        ready:
          "Заказ отмечается готовым, и покупатель получает уведомление. Ничего не отгружается, строки и резервы не меняются.",
        shipped:
          "Заказ отмечается отгруженным, покупатель получает уведомление. Резервы под неотгруженный остаток снимаются, отгруженные количества не меняются.",
        closed:
          "Заказ закрывается. Покупатель уведомление не получает; строки, отгрузки и резервы не меняются.",
      },
      facilityFenced:
        "Заказ отгружается с площадки вне вашего доступа. Отгрузку, статус и заказы на производство ведёт сотрудник этой площадки.",
      overrideReason: "Почему статус меняется вручную?",
      overrideConfirm: "Изменить статус",
      overrideKeep: "Оставить как есть",
      overriding: "Сохраняем…",
      overrideFailed: "Не удалось изменить статус заказа.",
      overrideError: {
        STATUS_REGRESSION: "Заказ можно перевести только вперёд. Выберите следующий этап.",
        STATUS_OVERRIDE_NOT_ALLOWED:
          "Этот этап нельзя установить вручную. Для производства, доставки и отмены есть свои действия.",
        ORDER_NOTHING_SHIPPED:
          "По этому заказу ещё ничего не отгружено. Такой заказ отменяют, а не отмечают отгруженным.",
        SHIPMENT_OPEN:
          "Отгрузка по этому заказу ещё открыта. Сначала отгрузите или отмените её на складе.",
        ORDER_TERMINAL: "Заказ закрыт или отменён, его статус больше нельзя изменить.",
        ORDER_HAS_ACTIVE_WOS:
          "Сначала нужно отменить заказы на производство по этому заказу клиента в разделе «Производство».",
      },
    },
  },

  shipments: {
    status: {
      picking: "Сборка",
      packed: "Упаковано",
      shipped: "Отгружено",
      delivered: "Доставлено",
      cancelled: "Отменено",
    },
    deliveryNote: "Накладная (PDF)",
    deliveryNoteRender: "Сформировать накладную",
    deliveryNoteRendering: "Формируем…",
    deliveryNoteMissing: "Накладная не сформирована.",
    deliveryNoteFailed: "Не удалось сформировать накладную.",
    deliveryNoteGlyphs:
      "Эти символы есть в заказе (покупатель, адрес доставки или названия товаров) или в колонтитулах документов в настройках продаж.",
    deliveryNoteUnpriced: "Накладная без цен (PDF)",
  },

  documents: {
    glyphsNamed: "Шрифты документа не могут напечатать эти символы: {{characters}}.",
    glyphsUnnamed: "В документе есть символы, которые шрифты документа не могут напечатать.",
    glyphsMore: "{{list}} и ещё {{rest}}",
  },

  floor: {
    ship: {
      tile: "Отгрузка",
      tileSub: "Собрать, упаковать и отгрузить заказы",
      queueTitle: "Очередь отгрузки",
      queueEmpty: "На этой площадке нечего отгружать.",
      badge: "Зарезервировано строк: {{reserved}} из {{total}}",
      title: "Сборка {{orderNo}}",
      open: "Начать сборку",
      scan: "Отсканируйте бобину",
      lineOpen: "Осталось собрать: {{open}} {{uom}}",
      // The line the unit card prints: three interpolations joined by middle
      // dots. The plan printed it identical to en; the value gate (check 4)
      // reads an identical ru value as untranslated, so the remaining length
      // carries its noun here — «остаток 1600 м» is what a storekeeper says.
      unitLine: "{{serial}} · {{location}} · остаток {{remaining}} {{uom}}",
      qty: "Длина к отбору",
      remains: "отбор {{qty}} {{uom}} → останется {{remaining}} {{uom}}",
      confirm: "Подтвердить отбор",
      fullUnit: "Взять единицу целиком",
      pack: "Упаковать",
      ship: "Отгрузить",
      carrier: "Перевозчик",
      tracking: "Номер отслеживания",
      last: "Эта отгрузка завершает заказ.",
      reservedForOther: "Зарезервировано для {{ref}} — не для этого заказа.",
      notReserved: "Эта единица не зарезервирована для этого заказа.",
      override: "Всё равно взять (администратор)",
      notShippableLocation: "Сначала разместите её — она ещё в зоне {{kind}}.",
      exceedsUnit: "На этой единице осталось только {{remaining}} {{uom}}.",
      exceedsLine: "По этой строке осталось только {{remaining}} {{uom}}.",
      alreadyPicked:
        "С этой единицы уже отбирали в этой отгрузке — отмените отбор, чтобы изменить длину.",
      unpick: "Отменить отбор",
      pending: "Отборов ждут отправки: {{count}}",
      deliveryNote: "Накладная",
      deliveryNoteOffline: "Для накладной нужна связь.",
      deliveryNoteQueued:
        "Упаковка сохранена на устройстве. Накладная будет сформирована, когда упаковка будет отправлена.",
      deliveryNoteNone: "Накладная не сформирована.",
      deliveryNoteDraw: "Сформировать накладную",
      deliveryNoteDrawing: "Формируем…",
      // ---- the screens' own states (en carries the reasoning per key) ------
      queueEmptyBody: "Заказы появляются здесь после подтверждения для этой площадки.",
      queueLoadFailedTitle: "Не удалось загрузить очередь отгрузки",
      queueLoadFailedBody: "Это не значит, что отгружать нечего. Попробуйте ещё раз.",
      // Completes `inventory.floor.cache.needConnectionBody` («…чтобы загрузить
      // {{what}}»), so both nouns stand in the accusative.
      thisQueue: "очередь отгрузки",
      thisOrder: "этот заказ",
      loadFailedTitle: "Не удалось загрузить отгрузку по этому заказу",
      loadFailedBody: "Попробуйте ещё раз или уточните в офисе, открыт ли ещё заказ.",
      openFailedTitle: "Не удалось начать сборку",
      openNeedsSignal:
        "Сохранено на этом устройстве. Отгрузка откроется, как только появится связь, и тогда начнётся сборка.",
      failedTitle: "Не получилось",
      doneBody: "Эта отгрузка закрыта. Собирать по ней больше нечего.",
      scanNeedsSignal: "Этой единицы нет в плане сборки, а чтобы найти её, нужна связь.",
      scanNoMatch: "Единица хранения «{{payload}}» не найдена.",
      chooseLine: "Выберите строку, для которой эта единица.",
      noOpenLines: "Сейчас по этому заказу больше нечего собирать.",
      qtyInvalid: "Введите количество больше нуля, не более трёх знаков после запятой.",
      pickedRow: "{{qty}} {{uom}} с {{serial}}",
      packedBody: "Упаковано. Укажите перевозчика, если он известен, и отгружайте.",
      notShippableNoLot:
        "У этой единицы нет партии, поэтому отгрузить её нельзя. Сначала оприходуйте её на партию.",
      notShippableWrongProduct:
        "На этой единице другой товар, не тот, что в строке заказа. Отсканируйте единицу заказанного товара.",
    },
  },

  export: {
    button: "Выгрузить CSV",
    walking: "Выгружаем… строк: {{count}}",
    cappedNote:
      "Строк в файле: {{count}}. Под эти фильтры подходит больше строк, чем помещается в один файл, — сузьте фильтры, чтобы выгрузить остальное.",
    failed: "Не удалось выгрузить список — файл не сохранён.",
    list: {
      orders: "Заказы",
      quotes: "Предложения",
      customers: "Клиенты",
      opportunities: "Сделки",
    },
    scopeAssigned: {
      orders: "заказы закреплённых клиентов и собственные заказы этой учётной записи",
      quotes: "предложения закреплённых клиентов и собственные предложения этой учётной записи",
      customers: "только закреплённые клиенты",
      opportunities: "сделки закреплённых клиентов и собственные сделки этой учётной записи",
    },
    scopeReadable: "строки, доступные этой учётной записи",
    filters: "фильтры: {{filters}}",
    noFilters: "без фильтров",
    sortAsc: "сортировка по {{column}}, по возрастанию",
    sortDesc: "сортировка по {{column}}, по убыванию",
    exportedAt: "выгружено {{at}}",
    complete: "все подходящие строки: {{count}}",
    capped: "первые подходящие строки: {{count}}; есть ещё",
    moneyOmitted: "денежные столбцы, недоступные этой учётной записи, не включены",
    punctuationCommaDot: "разделитель — запятая, десятичная точка",
    punctuationSemicolonComma: "разделитель — точка с запятой, десятичная запятая",
  },
  tasks: {
    title: "Задачи",
    views: { myDay: "Мой день", overdue: "Просроченные" },
    scopeLabel: "Чьи задачи",
    scopeMine: "Мои",
    scopeAll: "Все",
    statusLabel: "Показать",
    statusOpen: "Открытые",
    statusDone: "Выполненные",
    statusCancelled: "Закрытые",
    new: "Новая задача",
    loading: "Загрузка задач…",
    error: "Не удалось загрузить задачи",
    tableCaption: "Задачи",
    emptyOpenTitle: "Задач нет",
    emptyOpenBody:
      "Здесь появляются добавленные вами задачи, а также задачи, созданные, когда с клиентом не удалось связаться или он не ответил.",
    emptyDoneTitle: "Выполненных задач нет",
    emptyDoneBody: "Здесь перечислены выполненные задачи, сначала последние.",
    emptyCancelledTitle: "Закрытых задач нет",
    emptyCancelledBody:
      "Здесь появляются задачи, которые закрылись сами, и задачи, которые кто-то отозвал.",
    emptyFilteredTitle: "Задач по этому клиенту нет",
    emptyFilteredBody: "Сбросьте фильтр по клиенту, чтобы увидеть все задачи.",
    filterCustomer: "Клиент",
    filterCustomerHint: "Название клиента",
    filterAll: "Все клиенты",
    colSubject: "По чему",
    colWhat: "Что сделать",
    colOrigin: "Источник",
    colDue: "Срок",
    colAssignee: "Исполнитель",
    colStatus: "Статус",
    colActions: "Действия",
    kind: {
      call: "Звонок",
      meeting: "Встреча",
      todo: "Дело",
    },
    reason: {
      READY_NOT_COLLECTED: "Готовый заказ не забрали",
      QUOTE_NO_ANSWER: "Нет ответа на предложение",
      CUSTOMER_OPTED_OUT: "Клиент отказался от сообщений",
      NO_CHANNEL: "У клиента нет канала для сообщений",
      other: "Связаться с клиентом",
    },
    origin: {
      manual: "Добавлена вручную",
      reminder_cap_out: "Напоминания исчерпаны",
      push_no_channel: "Сообщение не отправлено",
    },
    state: {
      open: "Открыта",
      done: "Выполнена",
      cancelled: "Закрыта автоматически",
    },
    overdue: "Просрочена",
    doneAt: "Выполнена {{when}}",
    cancelledAt: "Закрыта {{when}}",
    cancelReason: {
      PREMISE_ENDED:
        "Больше не нужна: заказ или предложение продвинулись дальше, или клиент в архиве.",
      WITHDRAWN: "Отозвана. Никому больше не нужно её выполнять.",
    },
    subjectNone: "Без клиента и документа",
    complete: "Выполнить",
    completing: "Выполнение…",
    completeFailed: "Не удалось отметить задачу выполненной.",
    notOpen:
      "Задача уже закрыта — её выполнил кто-то другой или она закрылась сама. Список обновлён.",
    notFound: "Эта задача вам больше не видна. Список обновлён.",
    notYours: "Выполнить задачу может только её исполнитель или администратор.",
    createTitle: "Новая задача",
    kindLabel: "Вид",
    titleLabel: "Что сделать",
    titleRequired: "Опишите задачу.",
    titleTooLong: "Не больше 200 символов.",
    noteLabel: "Примечание",
    noteTooLong: "Примечание — не больше 2000 символов.",
    dueLabel: "Срок",
    dueRequired: "Выберите срок.",
    subjectLabel: "По чему",
    customerLabel: "Клиент",
    customerHint: "Начните вводить название клиента (необязательно)",
    customerNone: "Без клиента",
    quoteLabel: "Предложение",
    orderLabel: "Заказ",
    documentNone: "Нет",
    assigneeLabel: "Исполнитель",
    assigneeMe: "Я",
    assigneeNotEligible:
      "Этому сотруднику нельзя поручить задачу: выберите активного менеджера по продажам или администратора, которому доступны клиент, предложение или заказ задачи.",
    subjectNotFound: "Этот клиент, предложение или заказ вам не доступен.",
    subjectMismatch: "Предложение и заказ должны относиться к выбранному клиенту.",
    createFailed: "Не удалось создать задачу.",
    create: "Создать задачу",
    creating: "Создание…",
    cardTitle: "Открытые задачи",
    cardEmptyOrder: "Открытых задач по этому заказу нет.",
    cardEmptyQuote: "Открытых задач по этому предложению нет.",
    cardError: "Не удалось загрузить открытые задачи",
    cardMore: "Остальные открытые задачи — на экране «Задачи».",
    add: "Добавить задачу",
    openList: "Открыть список задач",
    filterDue: "Срок",
    dueAny: "Любой день",
    due: {
      overdue: "Просроченные",
      today: "Сегодня",
      through_today: "Сегодня и раньше",
      upcoming: "Ближайшие 7 дней",
    },
    filterAssignee: "Исполнитель",
    assigneeAny: "Любой",
    clearFilters: "Сбросить фильтры",
    emptyFiltersTitle: "Под эти фильтры задачи не подходят",
    emptyFiltersBody: "Расширьте срок, клиента или исполнителя.",
    colKind: "Вид",
    dealLabel: "Сделка",
    dealNone: "Без сделки",
    cardEmptyDeal: "Открытых задач по этой сделке нет.",
    cardEmptyCustomer: "Открытых задач по этому клиенту нет.",
    subjectMismatchDeal:
      "Сделка должна относиться к выбранному клиенту, а предложение из другой сделки нельзя указать вместе с ней.",
    subjectNotFoundDeal: "Эта сделка вам недоступна.",
    move: "Перенести на другой день",
    moveTitle: "Перенести на другой день",
    moveDueLabel: "Новый срок",
    moveConfirm: "Перенести",
    moving: "Переносим…",
    moveFailed: "Не удалось перенести задачу.",
    edit: "Изменить",
    editTitle: "Изменить задачу",
    editConfirm: "Сохранить изменения",
    editing: "Сохранение…",
    editFailed: "Не удалось сохранить задачу.",
    systemWords: "Текст этой задачи составила система, поэтому его нельзя изменить.",
    changeNotYours: "Изменить задачу может только её исполнитель или администратор.",
    reassign: "Передать",
    reassignTitle: "Передать задачу",
    reassignLabel: "Кому",
    reassignConfirm: "Передать",
    reassigning: "Передаём…",
    reassignFailed: "Не удалось передать задачу.",
    withdraw: "Отозвать",
    withdrawTitle: "Отозвать эту задачу?",
    withdrawBody: "Она перейдёт в закрытые как отозванная. Ничего не удаляется.",
    withdrawConfirm: "Отозвать",
    withdrawing: "Отзываем…",
    withdrawFailed: "Не удалось отозвать задачу.",
    withdrawNotYours:
      "Отозвать задачу может только тот, кто поставил её себе сам, или администратор.",
    notWithdrawable:
      "Эту задачу создала система, поэтому её нельзя отозвать. Она закроется сама, когда отпадёт причина.",
    withdrawn: "Отозвана",
  },
  settings: {
    title: "Настройки продаж",
    loading: "Загрузка настроек…",
    error: "Не удалось загрузить настройки продаж",
    readOnlyNote:
      "Вы можете просматривать эти настройки. Изменяют их администраторы группы и площадки.",
    engineTitle: "Автоматические проверки",
    engineLastRan: "Последний запуск: {{ago}} ({{when}})",
    engineNotYet: "Ещё не запускались.",
    engineBody:
      "Примерно раз в минуту система отправляет напоминания, закрывает просроченные предложения и подтверждает доставки.",
    activeSince:
      "Напоминания отправляются только по заказам и предложениям, которые стали готовы или были отправлены начиная с {{when}}.",
    agoNow: "меньше минуты назад",
    agoMinutes: "{{n}} мин назад",
    agoHours: "{{n}} ч назад",
    agoDays: "{{n}} дн. назад",
    readyTitle: "Готовый заказ не забрали",
    readyBody:
      "Напоминать клиенту, что готовый заказ ждёт его. После последнего напоминания менеджер получит задачу.",
    followUpTitle: "Напоминание о предложении",
    followUpBody:
      "Напоминать клиенту, который не ответил на предложение. Прекращается после ответа, затем менеджер получит задачу.",
    afterDays: "Дней между напоминаниями",
    maxSends: "Напоминаний не больше",
    offHint: "0 отключает сообщения; менеджер всё равно получит задачу.",
    tiersTitle: "По ценовым категориям",
    tiersBody: "Оставьте ячейку пустой, чтобы действовала общая настройка выше.",
    colTier: "Ценовая категория",
    colReadyAfter: "Готов: дней между",
    colReadyMax: "Готов: не больше",
    colFollowAfter: "Предложение: дней между",
    colFollowMax: "Предложение: не больше",
    tiersCaption: "Напоминания по ценовым категориям",
    inherit: "По умолчанию: {{value}}",
    quietTitle: "Тихие часы",
    quietBody: "В это время сообщения клиентам не отправляются (по времени завода).",
    quietStart: "С",
    quietEnd: "До",
    autoConfirmTitle: "Автоматическое подтверждение доставки",
    autoConfirmBody:
      "Неподтверждённый отгруженный заказ считается доставленным через столько дней.",
    autoConfirmDays: "Дней после отгрузки",
    pricesTitle: "Цены, которые видит клиент",
    trackingPrices: "Показывать цены на странице отслеживания заказа",
    deliveryNotePrices: "Показывать цены в накладной",
    save: "Сохранить настройки",
    saving: "Сохранение…",
    saved: "Сохранено.",
    saveFailed: "Не удалось сохранить настройки.",
    fixFields: "Некоторые значения вне допустимого диапазона — см. отмеченные поля.",
    range: "Введите целое число от {{min}} до {{max}}.",
    time: "Введите время в часах и минутах, например 21:00.",
    stagesTitle: "Этапы воронки",
    stagesBody:
      "Этапы сделки по порядку. Вероятность взвешивает прогноз; сделка дольше срока отмечается как зависшая.",
    stagesCaption: "Этапы воронки",
    stagesColStage: "Этап",
    stagesColProbability: "Вероятность, %",
    stagesColRotting: "Зависает через, дней",
    stagesRottingNever: "Никогда",
    stagesRottingHint: "Оставьте пустым — и сделка на этом этапе не зависает.",
    stagesProbabilityRange: "Введите целое число от 0 до 100.",
    stagesRottingRange: "Введите целое число дней от 1 до 365 или оставьте поле пустым.",
    stagesSave: "Сохранить",
    stagesSaving: "Сохранение…",
    stagesSaved: "Сохранено.",
    stagesSaveFailed: "Не удалось сохранить этап.",
    stagesLoading: "Загрузка этапов воронки…",
    stagesError: "Не удалось загрузить этапы воронки",
    stagesGone: "Этого этапа больше нет. Список обновлён.",
    stagesRefused: "Этапы воронки не показываются для вашей роли",
  },
  reviews: {
    title: "Согласования",
    intro: "Что ждёт решения, сначала самое давнее.",
    kind: {
      quote_margin_breach: "Маржа ниже порога · предложение",
      order_margin_breach: "Маржа ниже порога · заказ",
      price_override: "Индивидуальная цена",
      credit_block: "Кредитный стоп",
      fulfillment_degradation: "Изменилось обеспечение",
    },
    filterKind: "Вид",
    amount: "Сумма",
    amountUnit: "Цена за единицу",
    impact: "Влияние",
    requester: "Запросил",
    age: "Ждёт дней: {{count}}",
    ageToday: "Поступило сегодня",
    open: "Открыть документ",
    openCustomerPricing: "Открыть цены клиента",
    line: "Строка {{line}}",
    lineBreach: "{{margin}}% при пороге {{guard}}%",
    lineImpact: "Разница: {{amount}}",
    approve: "Согласовать",
    reject: "Отклонить",
    deciding: "Сохраняем решение…",
    note: "Комментарий",
    noteRequired: "Для отклонения нужна причина.",
    thresholdGroup:
      "Здесь нужен администратор группы: отклонение больше 2 пунктов или строка на крупную сумму.",
    cannotDecide: "Решение по этой карточке — не в ваших правах.",
    thresholdOverride:
      "Это согласует администратор группы: индивидуальная цена ниже порога более чем на 2 пункта.",
    informational: "Решать нечего — заказ уже создан. Эта карточка просто уведомляет.",
    departed:
      "{{no}} больше не ждёт решения — его уже согласовали на другом уровне или отозвал администратор.",
    creditApprove: "Снять стоп",
    creditPrepay: "Отметить предоплату",
    creditUphold: "Оставить стоп",
    creditOver: "Превышение лимита на {{amount}}",
    creditPrepayOnly: "Этот клиент работает по предоплате.",
    decideFailed: "Не удалось сохранить решение.",
    emptyTitle: "Ничего не ждёт решения",
    emptyBody:
      "Здесь появляются отклонения по марже, индивидуальные цены, кредитные стопы и изменения обеспечения.",
    error: "Не удалось загрузить согласования",
  },

  // §4.9 — аналитика продаж (P3-E PR-E3, D-293). «Ценовая категория» — тот же
  // термин, что и во всём этом файле; новое слово для того же понятия сделало
  // бы оба нечитаемыми.
  analytics: {
    title: "Аналитика продаж",
    intro: "Как идут дела у отдела — по менеджерам и ценовым категориям.",
    loading: "Загружаем показатели…",
    error: "Не удалось загрузить аналитику",
    empty: "За этот период не было ни предложений, ни заказов.",
    emptyBody: "Попробуйте выбрать более длинный период.",
    managerYou: "Вы",
    rangeFrom: "С",
    rangeTo: "По",
    rangeShown: "С {{from}} по {{to}}",
    tierFilter: "Ценовая категория",
    allTiers: "Все категории",
    lastMonth: "Последние 30 дней",
    lastQuarter: "Последние 90 дней",
    freshness:
      "Окончательно посчитано по {{date}}. Более поздние дни считаются на лету и ещё могут измениться.",
    freshnessNone:
      "Окончательных данных пока нет. Все дни здесь считаются на лету и ещё могут измениться.",
    colManager: "Менеджер",
    colTier: "Ценовая категория",
    colCurrency: "Валюта",
    colQuotesSent: "Отправлено предложений",
    colWinRate: "Доля принятых",
    colOffListRate: "Строки вне прайса",
    colBreaches: "Отклонения по марже",
    colOrders: "Заказы",
    colOrderValue: "Сумма заказов",
    colOffcutValue: "Выручка с остатков",
    tableCaption: "Показатели продаж по менеджеру, ценовой категории и валюте",
    noRate: "—",
    noRateHint: "За этот период предложения не отправлялись — доли нет.",
    valuesHidden: "Суммы заказов скрыты для вашей роли.",
    trendTitle: "Доля строк вне прайса по дням",
    trendBody: "Какая часть строк с ценой продана не по прайсу, по дням.",
    trendEmpty: "За этот период нет строк с ценой.",
    drillTitle: "Строки вне прайса — {{tier}}",
    drillCaption: "Строки, из которых складывается доля вне прайса",
    drillDocument: "Документ",
    drillLine: "Строка",
    drillDated: "Учтено",
    drillWhat: "Что продано",
    drillQty: "Количество",
    drillResolved: "Цена по прайсу",
    drillTyped: "Цена продажи",
    drillDelta: "Разница",
    drillBreach: "Маржа",
    drillBreached: "Ниже порога",
    drillEmpty: "За этот период строк вне прайса нет.",
    drillError: "Не удалось загрузить строки вне прайса",
    drillTruncated:
      "Строк больше, чем помещается на страницу. Сузьте период, чтобы увидеть остальные.",
    forecastTitle: "Прогноз по воронке",
    forecastBody: "Открытые сделки по месяцам закрытия, взвешенные по этапу, рядом с выигранными.",
    forecastLink: "Открыть прогноз",
  },

  nextStep: {
    label: "Следующий шаг",
    none: "Нет следующего шага",
    on: "{{kind}} · {{date}}",
    today: "Сегодня",
    overdue: "Просрочено",
    overdueOn: "Просрочено с {{date}}",
    schedule: "Запланировать",
    scheduleAria: "Запланировать следующий шаг: {{name}}",
  },

  peek: {
    open: "Просмотр",
    openNamed: "Просмотр: {{name}}",
    close: "Закрыть",
    openFull: "Открыть полностью",
    kind: {
      customer: "Клиент",
      quote: "Предложение",
      order: "Заказ",
      opportunity: "Сделка",
    },
    title: "{{kind}} {{name}}",
    loading: "Загрузка…",
    notAvailable: "Эта запись вам недоступна",
    error: "Не удалось загрузить запись",
    tier: "Ценовая категория",
    status: "Статус",
    owner: "Ответственный",
    ownerNone: "Без ответственного",
    primaryContact: "Основной контакт",
    primaryContactNone: "Основного контакта нет",
    openTasks: "Открытые задачи",
    openTasksNone: "Открытых задач нет",
    openDeals: "Открытые сделки",
    openDealsNone: "Открытых сделок нет",
    latestQuotes: "Последние предложения",
    latestQuotesNone: "Предложений пока нет",
    latestOrders: "Последние заказы",
    latestOrdersNone: "Заказов пока нет",
    viewed: "Просмотрено",
    customer: "Клиент",
    peekCustomer: "Просмотр клиента",
    total: "Сумма",
    validUntil: "Действует до",
    revision: "Версия {{n}}",
    lines: "Строки",
    linesMore: "Остальные строки — на полной странице: {{count}}",
    deal: "Сделка",
    dealNone: "Без сделки",
    dealHidden: "В сделке, которая вам недоступна",
    customerPo: "Номер заказа клиента",
    customerPoNone: "Без номера",
    credit: "Кредит",
    shippedOf: "Отгружено {{shipped}} из {{ordered}} {{uom}}",
    promisePending: "Дата готовности ещё не назначена",
    promiseDate: "Дата готовности: {{date}}",
  },

  pipeline: {
    views: { rotting: "Зависшие", needsNextStep: "Нужен следующий шаг" },
    title: "Воронка",
    tabsAria: "Страницы воронки",
    tabs: { board: "Доска", list: "Список", forecast: "Прогноз" },
    newDeal: "Новая сделка",
    loading: "Загрузка воронки…",
    filterOwner: "Ответственный",
    anyOwner: "Любой ответственный",
    filterCustomer: "Клиент",
    // Short enough for the picker's box at desk width (the walk found the
    // longer hint clipped on the board and the forecast).
    filterCustomerHint: "Название клиента",
    anyCustomer: "Все клиенты",
    clearFilters: "Сбросить фильтры",
    status: { open: "Открыта", won: "Выиграна", lost: "Проиграна" },
    probability: "{{pct}} %",
    stageWithProbability: "{{stage}} · {{pct}} %",
    rotting: "Без движения {{count}} дн",
    inStageDays: "{{count}} дн",
    closeMonth: "Закрытие: {{month}}",
    closeMonthPassed: "Месяц закрытия прошёл",
    noValue: "Без суммы",
    weightedValue: "Взвешенно {{amount}}",
    lostBecause: "Проиграна: {{reason}}",
    owner: "Ответственный",
    ownerNone: "Без ответственного",
    noStagesTitle: "Этапов воронки пока нет",
    noStagesBody:
      "Этапы воронки приходят с отраслевым пакетом, а в этой системе их пока нет. Попросите администратора группы применить обновление отраслевого пакета.",
    noStagesAdmin:
      "В системе нет этапов воронки. Примените обновление отраслевого пакета, чтобы добавить их.",
    noStagesAction: "Посмотреть обновление отраслевого пакета",
    board: {
      aria: "Открытые сделки по этапам",
      columnAria: "{{stage}}: открытых сделок {{count}}",
      count: "Сделок: {{count}}",
      rottingCount: "Зависших: {{count}}",
      openTotal: "Открыто {{amount}}",
      weightedTotal: "Взвешенно {{amount}}",
      unvaluedCount: "Без суммы: {{count}}",
      showMore: "Показать ещё",
      showMoreFailed: "Не удалось загрузить ещё сделки.",
      columnEmpty: "На этом этапе открытых сделок нет.",
      emptyTitle: "Открытых сделок пока нет",
      emptyBody: "Заведите сделку по клиенту и передвигайте её по этапам по мере продвижения.",
      filteredEmptyTitle: "Под эти фильтры открытые сделки не подходят",
      filteredEmptyBody: "Сбросьте ответственного или клиента, чтобы увидеть все открытые сделки.",
      error: "Не удалось загрузить доску",
      wonZone: "Выиграна",
      lostZone: "Проиграна",
      move: "Переместить",
      moveAria: "Переместить: {{title}}",
      keyboardHint:
        "Пробел — взять сделку, стрелки — выбрать этап, пробел или Enter — положить, Escape — отменить.",
      livePicked: "Сделка {{title}} взята с этапа «{{stage}}».",
      liveOver: "Над этапом «{{stage}}».",
      liveOverWon: "Над зоной «Выиграна». Отпустите, чтобы отметить сделку выигранной.",
      liveOverLost: "Над зоной «Проиграна». Отпустите, чтобы указать причину.",
      liveDropped: "Сделка {{title}} перенесена на этап «{{stage}}».",
      liveCancelled: "Перемещение отменено. Сделка {{title}} снова на этапе «{{stage}}».",
      liveWon: "Сделка {{title}} отмечена выигранной.",
      liveLost: "Сделка {{title}} отмечена проигранной.",
      pending: "Сохраняем перемещение…",
      network: "Перемещение не сохранилось. Проверьте связь и повторите.",
      tryAgain: "Повторить",
      stagesAria: "Этапы",
      stageChip: "{{stage}}: {{count}}",
    },
    list: {
      title: "Сделки",
      caption: "Сделки",
      loading: "Загрузка сделок…",
      error: "Не удалось загрузить сделки",
      search: "Поиск сделок",
      searchHint: "Название, номер или клиент",
      filterStatus: "Статус",
      filterStage: "Этап",
      anyStage: "Все этапы",
      filterCloseFrom: "Закрытие с",
      filterCloseTo: "Закрытие по",
      filterRotting: "Только зависшие",
      filterNeedsNextStep: "Нужен следующий шаг",
      filterLostReason: "Причина проигрыша",
      anyLostReason: "Любая причина",
      colDeal: "Сделка",
      colCustomer: "Клиент",
      colStage: "Этап",
      colValue: "Сумма",
      colWeighted: "Взвешенная",
      colCloseMonth: "Месяц закрытия",
      colNextStep: "Следующий шаг",
      colInStage: "На этапе",
      colOwner: "Ответственный",
      colStatus: "Статус",
      colUpdated: "Изменена",
      sortAsc: "Сортировать по «{{column}}», по возрастанию",
      sortDesc: "Сортировать по «{{column}}», по убыванию",
      emptyTitle: "Сделок пока нет",
      emptyBody: "Заведите сделку по клиенту — и она появится здесь.",
      filteredEmptyTitle: "Под эти фильтры сделки не подходят",
      filteredEmptyBody:
        "Расширьте статус, этап, ответственного, клиента, месяцы закрытия, зависшие, следующий шаг, причину проигрыша или поиск.",
    },
    deal: {
      number: "Сделка {{no}}",
      loading: "Загрузка сделки…",
      error: "Не удалось загрузить сделку",
      notAvailableTitle: "Эта сделка вам недоступна",
      notAvailableBody: "Возможно, у неё другой ответственный или ваша роль не видит её.",
      customer: "Клиент",
      stage: "Этап",
      inStage: "На этом этапе, дней: {{count}}",
      nextStep: "Следующий шаг",
      move: "Переместить",
      markWon: "Отметить выигранной",
      markLost: "Отметить проигранной",
      reopen: "Вернуть в работу",
      reopening: "Возвращаем…",
      reopenFailed: "Не удалось вернуть сделку в работу.",
      detailsTitle: "Сведения",
      titleLabel: "Название",
      valueLabel: "Сумма",
      valueHint: "Сумма, которую заплатит клиент, с НДС",
      currencyLabel: "Валюта",
      closeMonthLabel: "Месяц закрытия",
      ownerLabel: "Ответственный",
      save: "Сохранить",
      saving: "Сохранение…",
      saved: "Сохранено.",
      saveFailed: "Не удалось сохранить сделку.",
      closedFields:
        "У закрытой сделки сумма и месяц закрытия не меняются. Верните её в работу, чтобы изменить их.",
      quotesTitle: "Предложения",
      quotesEmpty: "К этой сделке не привязано ни одного предложения.",
      createQuote: "Новое предложение",
      linkQuote: "Привязать предложение",
      linkTitle: "Привязать предложение",
      linkHint: "Открытые предложения клиента, не привязанные к сделке.",
      linkEmpty: "У этого клиента нет открытых предложений без сделки.",
      linkConfirm: "Привязать",
      linking: "Привязываем…",
      linkFailed: "Не удалось привязать предложение.",
      linkListError: "Не удалось загрузить предложения этого клиента.",
      linkUnchecked: "Не удалось проверить, привязано ли оно к другой сделке",
      linkNoneYet:
        "Среди загруженных предложений нет таких, которые можно привязать. «Показать ещё» загрузит более ранние.",
      unlink: "Отвязать",
      unlinking: "Отвязываем…",
      unlinkFailed: "Не удалось отвязать предложение.",
      acceptedNotice: "Клиент принял {{quote}}. Отметьте сделку выигранной с этим предложением.",
      acceptedNoticeReader: "Клиент принял {{quote}}, а сделка всё ещё открыта.",
      markWonWith: "Выиграна с {{quote}}",
      wonCancelled:
        "Сделка была выиграна, но её заказ {{order}} потом отменили. Верните её в работу, чтобы продолжить.",
      wonCancelledNoNumber:
        "Сделка была выиграна, но её заказ потом отменили. Верните её в работу, чтобы продолжить.",
      wonWithQuoteAndOrder: "Выиграна с предложением {{quote}} и заказом {{order}}",
      wonWithQuote: "Выиграна с предложением {{quote}}",
      wonWithOrder: "Выиграна с заказом {{order}}",
      wonByAcceptance: "Выиграна, когда клиент принял предложение",
      wonByHand: "Отмечена выигранной вручную",
      closedAt: "Закрыта {{when}}",
      lostReason: "Почему проиграна",
      lostNote: "Примечание",
      tasksTitle: "Задачи",
      sourceTitle: "Источник",
      sourceConversation: "Начата из переписки",
      openConversation: "Открыть переписку",
    },
    forecast: {
      title: "Прогноз",
      loading: "Загрузка прогноза…",
      error: "Не удалось загрузить прогноз",
      rangeFrom: "С",
      rangeTo: "По",
      rangeInvalid: "Выберите конечный месяц не раньше начального и не позже чем через 24 месяца.",
      caption: "Прогноз, {{currency}}",
      colMonth: "Месяц",
      colDeals: "Сделки",
      colPipeline: "В воронке",
      colWeighted: "Взвешенно",
      colWon: "Выиграно за месяц",
      colUnvalued: "Без суммы",
      slipped: "Срок прошёл",
      slippedHint: "Открытые сделки, у которых прошёл месяц закрытия",
      cellLink: "Открыть сделки с закрытием: {{month}}",
      cancelledWins:
        "Выигранных сделок с отменённым заказом: {{count}}. Сумма по ним не учитывается.",
      scopeSales: "Ваши сделки и сделки по закреплённым за вами клиентам.",
      noValue: "Прогноз строится по суммам сделок, а они не показываются для вашей роли.",
      emptyTitle: "За эти месяцы прогнозировать нечего",
      emptyBody:
        "Здесь появляются открытые сделки с закрытием в этом периоде и сделки, выигранные в нём.",
      wonUnvalued: "Выиграно без суммы: {{count}}",
      filteredEmptyTitle: "За эти месяцы нет сделок, подходящих под фильтры",
      filteredEmptyBody:
        "Сбросьте фильтры по ответственному и клиенту, чтобы увидеть весь прогноз.",
    },
    create: {
      title: "Новая сделка",
      titleLabel: "Название",
      customerLabel: "Клиент",
      customerHint: "Начните вводить название клиента",
      valueLabel: "Сумма",
      valueHint: "Сумма, которую заплатит клиент, с НДС",
      currencyLabel: "Валюта",
      closeMonthLabel: "Месяц закрытия",
      stageLabel: "Этап",
      ownerLabel: "Ответственный",
      ownerMe: "Я",
      fromConversation: "Из переписки",
      confirm: "Создать сделку",
      creating: "Создание…",
      failed: "Не удалось создать сделку.",
      titleRequired: "Дайте сделке название.",
      titleTooLong: "Название — не больше 200 символов.",
      customerRequired: "Выберите клиента.",
      valueWaiting:
        "Валюта клиента ещё не загрузилась, поэтому сумму нельзя прочитать. Повторите через несколько секунд.",
      valueInvalid: "Введите сумму или оставьте поле пустым.",
      closeMonthRequired: "Выберите месяц, когда сделка должна закрыться.",
    },
    move: {
      title: "Переместить: {{title}}",
      stagesLabel: "Этап",
      closeLabel: "Закрыть сделку",
      current: "{{stage}}, сейчас",
      confirm: "Переместить",
      moving: "Перемещаем…",
      failed: "Не удалось переместить сделку.",
    },
    lose: {
      title: "Отметить проигранной: {{title}}",
      reasonLabel: "Почему сделка проиграна?",
      reasonRequired: "Выберите причину.",
      noteLabel: "Примечание",
      noteHint: "Что сказал клиент.",
      noteTooLong: "Примечание — не больше 2000 символов.",
      staysOpen: "Открытые предложения и задачи по этой сделке останутся открытыми.",
      confirm: "Отметить проигранной",
      saving: "Сохранение…",
      failed: "Не удалось отметить сделку проигранной.",
    },
    win: {
      title: "Отметить выигранной: {{title}}",
      body: "Выигранная сделка уходит с доски и учитывается в прогнозе в месяце закрытия.",
      confirm: "Отметить выигранной",
      saving: "Сохранение…",
      failed: "Не удалось отметить сделку выигранной.",
      pickLabel: "Чем выиграна сделка",
      pickHint:
        "Выберите предложение или заказ, по которому клиент купил; прогноз учтёт его сумму.",
      none: "Без документа. Прогноз учтёт сумму самой сделки.",
      optionQuote: "Предложение {{quote}}, заказ {{order}}",
      optionOrder: "Заказ {{order}}",
      empty: "У этого клиента нет предложений или заказов, которыми можно выиграть эту сделку.",
      noneYet:
        "Среди загруженных заказов нет таких, которыми можно выиграть эту сделку. «Показать ещё» загрузит более ранние.",
      listError: "Не удалось загрузить заказы этого клиента.",
      ordersRefused:
        "Ваша роль не видит заказы этого клиента, поэтому сделка будет выиграна по своей сумме.",
    },
    refusal: {
      OPPORTUNITY_STAGE_CHANGED:
        "Кто-то уже перенёс эту сделку на этап «{{stage}}». Она остаётся там.",
      OPPORTUNITY_NOT_OPEN: "Эта сделка уже закрыта.",
      OPPORTUNITY_NOT_CLOSED: "Эта сделка уже в работе.",
      OPPORTUNITY_NOT_FOUND: "Эта сделка вам больше не видна.",
      PIPELINE_STAGE_NOT_FOUND: "Этого этапа больше нет.",
      PIPELINE_NOT_CONFIGURED:
        "Этапы воронки не настроены, поэтому сделку создать нельзя. Примените обновление отраслевого пакета, чтобы добавить их.",
      OPPORTUNITY_OWNER_NOT_ELIGIBLE:
        "Этот сотрудник не может вести сделку. Выберите активного менеджера по продажам или администратора, которому доступен клиент.",
      OPPORTUNITY_CUSTOMER_MISMATCH:
        "Это предложение, заказ или переписка относится к другому клиенту.",
      OPPORTUNITY_WON_BY_ORDER:
        "Заказ клиента по этой сделке ещё действует. Вернуть сделку в работу можно после отмены заказа.",
      CUSTOMER_ARCHIVED: "Клиент в архиве, поэтому открытых сделок у него быть не может.",
      CUSTOMER_NOT_FOUND: "Этот клиент вам не доступен.",
      QUOTE_NOT_FOUND: "Это предложение вам недоступно.",
      ORDER_NOT_FOUND: "Этот заказ вам не доступен.",
      QUOTE_TERMINAL: "Работа с этим предложением завершена, поэтому его сделку изменить нельзя.",
      QUOTE_LINKED_ELSEWHERE:
        "Это предложение уже привязано к другой сделке. Сначала отвяжите его там.",
      QUOTE_NOT_CONVERTED: "Это предложение ещё не стало заказом.",
      ORDER_NOT_CONFIRMED: "Этот заказ ещё ждёт согласования.",
      ORDER_TERMINAL: "Этот заказ отменён.",
      ORDER_WON_ELSEWHERE: "Этот заказ уже закрыл другую сделку.",
      CONVERSATION_NOT_FOUND: "Переписка, из которой начата сделка, недоступна.",
      SOURCE_CONVERSATION_UNVERIFIABLE:
        "Сейчас не удалось проверить переписку. Повторите через минуту.",
      forbidden: "Ваша роль не может изменять эту сделку.",
      valueForbidden: "Ваша роль не может указывать сумму сделки.",
    },
  },

  cells: {
    owner: {
      edit: "Сменить ответственного: {{name}}",
      list: "Ответственный: {{name}}",
      filter: "Найти сотрудника",
      noMatch: "Никого не найдено.",
      adminOnly: "Ответственного за клиента назначает администратор группы или площадки.",
      changed: "Ответственного уже сменили: теперь это {{owner}}. Ячейка показывает текущего.",
      changedToNone: "Ответственного уже сняли: сейчас у клиента его нет.",
      changedUnnamed: "Ответственного уже сменили. Ячейка показывает текущего.",
      refused: "Ответственный не изменён: {{reason}}.",
      failed: "Ответственный не изменён: запрос остался без ответа. Повторите попытку.",
    },
    tags: {
      edit: "Изменить метки: {{name}}",
      list: "Метки: {{name}}",
      apply: "Применить",
      refused: "Не изменились метки: {{list}}.",
      refusedItem: "{{tag}} ({{reason}}, {{code}})",
      refusedCode: "{{tag}} ({{code}})",
      noAnswer: "{{tag}} (нет ответа)",
      signedOut: "{{tag}} (сессия завершена)",
      archivedOption: "{{name}} (в архиве)",
      failed: "Метки не изменены: запрос остался без ответа. Повторите попытку.",
    },
    stage: {
      edit: "Сменить этап: {{title}}",
      list: "Этап сделки «{{title}}»",
      closed:
        "У выигранной или проигранной сделки этап не меняется. Чтобы сменить его, верните сделку в работу на её странице.",
      notAllowed: "Ваша роль не может перемещать эту сделку.",
      failed: "Сделка не перемещена: запрос остался без ответа. Повторите попытку.",
    },
    signedOut: "Сессия завершилась, поэтому ничего не изменено. Войдите снова.",
    done: {
      header: "Выполнено",
      done: "Выполнено",
      notDone: "Не выполнено",
      edit: "Отметить выполненной: {{what}}",
      isDone: "Задача выполнена, и эту отметку не снять.",
      closed: "Задача закрыта без выполнения.",
      notAllowed: "Выполнить задачу может только её исполнитель или администратор.",
      failed: "Задача не отмечена выполненной: запрос остался без ответа. Повторите попытку.",
    },
  },
};
